/** ***********
*
* <esri-map.service.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  SanjayManral [sanjay.manral.external@airbus.com]
*
* All rights reserved.
**************/
/* eslint @typescript-eslint/no-explicit-any: 0 */

import { Beacon, EsriBeacon } from './../../models/beacon';
import { ElementRef, Injectable } from '@angular/core';
import { NgElement, WithProperties } from '@angular/elements';
import { loadModules } from 'esri-loader';
import { BehaviorSubject, concat, Observable, firstValueFrom, of } from 'rxjs';
import { PopInBeaconClusterComponent } from 'src/app/components/esri-map/pop-in/pop-in-beacon-cluster/pop-in-beacon-cluster.component';
import { PopInClusterComponent } from 'src/app/components/esri-map/pop-in/pop-in-cluster/pop-in-cluster.component';
import { PopInHistoryClusterComponent } from 'src/app/components/esri-map/pop-in/pop-in-history-cluster/pop-in-history-cluster.component';
import { PopInHistoryComponent } from 'src/app/components/esri-map/pop-in/pop-in-history/pop-in-history.component';
import { PICTO, PICTO_COLOR } from 'src/app/models/scope-management.model';
import { EsriQueryService } from '../esri-query/esri-query.service';
import { PopInZoneInfoComponent } from 'src/app/components/esri-map/pop-in/pop-in-zone-info/pop-in-zone-info.component';
import { MapScopeManagementService } from '../map-scope-management.service';
import { Asset } from 'src/app/models/asset';
import { Zone } from 'src/app/models/zone';
import { filter, map, take, tap, switchMap } from 'rxjs/operators';
import { Event as AssetEvent } from 'src/app/models/event';
import MapConstructor from 'esri/map';
import GraphicConstructor from 'esri/graphic';
import PointConstructor from 'esri/geometry/Point';
import ExtentConstructor from 'esri/geometry/Extent';
import ColorConstructor from 'esri/Color';
import SimpleLineSymbolConstructor from 'esri/symbols/SimpleLineSymbol';
import GraphicsLayerConstructor from 'esri/layers/GraphicsLayer';
import SpatialReferenceConstructor from 'esri/SpatialReference';
import ClassBreaksRendererConstructor from 'esri/renderers/ClassBreaksRenderer';
import SimpleFillSymbolConstructor from 'esri/symbols/SimpleFillSymbol';
import SimpleMarkerSymbolConstructor from 'esri/symbols/SimpleMarkerSymbol';
import PolylineConstructor from 'esri/geometry/Polyline';
import PolygonConstructor from 'esri/geometry/Polygon';
import TextSymbolConstructor from 'esri/symbols/TextSymbol';
import SymbolConstructor from 'esri/symbols/Symbol';
import PopupTemplateConstructor from 'esri/dijit/PopupTemplate';
import PopupMobileConstructor from 'esri/dijit/PopupMobile';
import GeometryConstructor from 'esri/geometry/Geometry';
import _webMercatorUtils from 'esri/geometry/webMercatorUtils';
import _arcgisUtil from 'esri/arcgis/utils';
import Multipoint from 'esri/geometry/Multipoint';
import { Instantiable } from 'src/app/models/utils';
import Layer from 'esri/layers/layer';
import { cloneDeep } from 'lodash';
import { ConfigurationStorageKeys, LocalStorageService } from '../local-storage.service';
import { Anchor } from 'src/app/models/anchor';
import { PopInAnchorClusterComponent } from 'src/app/components/esri-map/pop-in/pop-in-anchor-cluster/pop-in-anchor-cluster.component';
import { SignalSources } from 'src/app/models/signal-source';
import { OutdatedHorizonService } from '../outdated-horizon.service';
import { IndoorBuilding, IndoorBuildingOption, IndoorFloor, IndoorFloorOption, IndoorMapDataset, IndoorSite, IndoorSiteOption } from 'src/app/models/indoor-map';
import { IndoorMapDataService } from '../indoor-map-data.service';

/**
 * Add 'type' property to layer interface
 */
type LayerWithProps = Layer & { arcgisProps: { title: string } };
/** Declare arcgis API modules */
let arcgisUtils: typeof _arcgisUtil,
  Extent: typeof ExtentConstructor,
  SpatialReference: typeof SpatialReferenceConstructor,
  ClusterFeatureLayer: any,
  ClassBreaksRenderer: typeof ClassBreaksRendererConstructor,
  SimpleMarkerSymbol: typeof SimpleMarkerSymbolConstructor & { STYLE_SOLID: string },
  SimpleLineSymbol: typeof SimpleLineSymbolConstructor,
  Color: typeof ColorConstructor,
  Point: typeof PointConstructor,
  PopupTemplate: typeof PopupTemplateConstructor,
  GraphicsLayer: typeof GraphicsLayerConstructor,
  Graphic: Instantiable<Omit<typeof GraphicConstructor, 'geometry'> & { geometry: GeometryConstructor & Multipoint }> & typeof GraphicConstructor,
  TextSymbol: typeof TextSymbolConstructor,
  Polygon: typeof PolygonConstructor,
  webMercatorUtils: Omit<typeof _webMercatorUtils, 'geographicToWebMercator'> & { geographicToWebMercator(point: PointConstructor | PolygonConstructor): GeometryConstructor & { x: number, y: number } },
  SimpleFillSymbol: typeof SimpleFillSymbolConstructor,
  Polyline: typeof PolylineConstructor,
  Symbol: typeof SymbolConstructor;

export interface MapConfiguration {
  mapType?: 'satellite' | 'openStreetMap';
  displayZone?: boolean;
  displayBeacons?: boolean;
  displayAnchors?: boolean;
  displayIndoor?: boolean;
  displayAssetLabels?: boolean;
  disabledMovementStatus?: boolean;
  disabledOutdatedHorizon?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EsriMapService {
  private static readonly INDOOR_SITE_MIN_ZOOM = 13;
  private static readonly INDOOR_BUILDING_MIN_ZOOM = 16;
  private static readonly INDOOR_FLOOR_MIN_ZOOM = 18;

  private arcgisVersion = 'https://js.arcgis.com/3.27/';
  // private mapId = 'f543c39f06fb4bc4a09e2788fa85b81e';

  private options = {
    url: this.arcgisVersion,
    dojoConfig: {
      paths: {
        extras: '/assets/js'
      }
    }
  };

  /****** LOG SVG ******/
  private _baseBeaconSVG = PICTO.baseBeaconSVG;
  private _backgroundSVG = PICTO.backgroundSVG;

  private blue = [0, 133, 173];
  private white = [255, 255, 255];
  private gray = [127, 127, 127];

  private _selectedAsset = {};
  public selectedAsset$ = new BehaviorSubject<any>(this._selectedAsset);

  private selectedCluster = [];
  public selectedCluster$ = new BehaviorSubject<any>(this.selectedCluster);

  private selectedHistoryCluster = [];
  public selectedHistoryCluster$ = new BehaviorSubject<any>(this.selectedHistoryCluster);

  private selectedBeacon = {};
  public selectedBeacon$ = new BehaviorSubject<any>(this.selectedBeacon);

  private selectedAnchor = {};
  public selectedAnchor$ = new BehaviorSubject<any>(this.selectedAnchor);

  private selectedBeaconCluster = [];
  public selectedBeaconCluster$ = new BehaviorSubject<any>(this.selectedBeaconCluster);

  private selectedAnchorCluster = [];
  public selectedAnchorCluster$ = new BehaviorSubject<any>(this.selectedAnchorCluster);

  private selectedZone = {};
  public selectedZone$ = new BehaviorSubject<any>(this.selectedZone);
  private zoneDisplayed: boolean = false;

  private _selectedAssetHistory: any = null;
  public selectedAssetHistory$ = new BehaviorSubject<any>(this.selectedBeacon);

  private unselectedItem = undefined;
  public unselectedItem$ = new BehaviorSubject<any>(this.unselectedItem);

  public webMap: MapConstructor & {
    infoWindow: PopupMobileConstructor,
    onPanEnd: (event: ExtentConstructor) => void /* Trick to get the good type */,
    onZoomEnd: (event: ExtentConstructor) => void /* Trick to get the good type */,
  };
  public maps: { satellite?: MapConstructor & { infoWindow: PopupMobileConstructor }, openStreetMap?: MapConstructor & { infoWindow: PopupMobileConstructor } } = {};
  private _mapViewEl!: ElementRef<HTMLDivElement>;
  private _beaconsVisible = false;
  private _anchorsVisible = false
  private _assetLabelsVisible = false;
  private _disabledMovementStatus = false;
  private _disabledOutdatedHorizon = false;
  private _lastAssetForHistory!: Asset;
  private _lastHistory: any;
  private _indoorVisible = false;
  private _selectedIndoorSiteId: string | null = null;
  private _selectedIndoorBuildingId: string | null = null;
  private _selectedIndoorFloorId: string | null = null;
  private _selectedIndoorBuilding: IndoorBuilding | null = null;
  private _indoorDataset!: IndoorMapDataset;
  private _indoorDatasetPromise!: Promise<IndoorMapDataset>;
  private zones!: Zone[];
  private readonly STALE_PINPOINT_ALPHA = 0.36;
  public indoorSiteOptions$ = new BehaviorSubject<IndoorSiteOption[]>([]);
  public indoorBuildingOptions$ = new BehaviorSubject<IndoorBuildingOption[]>([]);
  public indoorFloorOptions$ = new BehaviorSubject<IndoorFloorOption[]>([]);
  public indoorLoading$ = new BehaviorSubject<boolean>(false);
  public indoorSitesLoadError$ = new BehaviorSubject<boolean>(false);
  public indoorSiteBuildingsLoading$ = new BehaviorSubject<boolean>(false);
  public indoorFloorDetailsLoading$ = new BehaviorSubject<boolean>(false);
  public indoorBuildingsDetailsLoading$ = new BehaviorSubject<boolean>(false);
  public selectedIndoorSiteId$ = new BehaviorSubject<string | null>(null);
  public selectedIndoorBuildingId$ = new BehaviorSubject<string | null>(null);
  public selectedIndoorFloorId$ = new BehaviorSubject<string | null>(null);
  public selectedIndoorBuilding$ = new BehaviorSubject<IndoorBuilding | null>(this._selectedIndoorBuilding);

  /**
   * Emit true when the asset graphic layer is ready. Aims to avoid asynchronous unexpected behaviour,
   * especially for the "jump to map" feature when it is triggered while the layer is not yet created
   */

  constructor(
    private esriQueryService: EsriQueryService,
    private _mapScopeService: MapScopeManagementService,
    private _localStorageService: LocalStorageService,
    private _outdatedHorizonService: OutdatedHorizonService,
    private _indoorMapDataService: IndoorMapDataService,
  ) {
  }

  /**
   * Load the Esri Map Javascript API into the div element
   * - handle the click event
   */
  public async loadWebMap(mapViewEl: ElementRef, appDataLoaded$: Observable<boolean>, mapConfig?: MapConfiguration) {
    await this._preLoadModules();
    await this._loadMapConfiguration(mapViewEl, appDataLoaded$, mapConfig);
  }

  private _initWebMapConfig() {
    this._attachOnClickEvent(this.webMap);

    // listen layer add event
    this.webMap.on('layer-add', (event: any) => {
      if (event.layer['customId'] === 'assetClusterFeatureLayer') {
        // the timout aims to avoid assetClusterFeatureLayer blinking when it is re drawed next to websocket update
        setTimeout.bind(this)(() => {
          this._removeFlagedLayers();
        }, 0);
      }
    });

    this.webMap.on('zoom-end', () => this._applyIndoorVisibility());
    // Indoor layers will be loaded lazily when user toggles Display Indoor ON
    this._applyIndoorVisibility();

  }

  /**
   * Pre load all the library modules
   */
  private async _preLoadModules() {
    const modulesNames = [
      'esri/arcgis/utils',
      'esri/geometry/Extent',
      'esri/SpatialReference',
      'extras/ClusterFeatureLayer',
      'esri/renderers/ClassBreaksRenderer',
      'esri/symbols/SimpleMarkerSymbol',
      'esri/symbols/SimpleLineSymbol',
      'esri/Color',
      'esri/geometry/Point',
      'esri/dijit/PopupTemplate',
      'esri/layers/GraphicsLayer',
      'esri/graphic',
      'esri/symbols/TextSymbol',
      'esri/geometry/Polygon',
      'esri/geometry/webMercatorUtils',
      'esri/symbols/SimpleFillSymbol',
      'esri/geometry/Polyline',
      'esri/symbols/Symbol'
    ];

    const modules = await loadModules(modulesNames, this.options);
    [
      arcgisUtils,
      Extent,
      SpatialReference,
      ClusterFeatureLayer,
      ClassBreaksRenderer,
      SimpleMarkerSymbol,
      SimpleLineSymbol,
      Color,
      Point,
      PopupTemplate,
      GraphicsLayer,
      Graphic,
      TextSymbol,
      Polygon,
      webMercatorUtils,
      SimpleFillSymbol,
      Polyline,
      Symbol
    ] = modules;
  }

  /**
   * The map configuration is loaded from the local storage, as it nevers changes
   * Once the app is initialized, the data is updated from the API
   */
  private async _loadMapConfiguration(mapViewEl: ElementRef, appDataLoaded$: Observable<boolean>, mapConfig?: MapConfiguration) {
    // boolean to optimize the API call if it is needed
    let configLoadedFromAPI = false;
    const maps = await concat(
        this._localStorageService.fetchLocalStorageValueByKey$(ConfigurationStorageKeys.mapData).pipe(
            take(1),
            map(({ value }) => value ? JSON.parse(value) : null),
            switchMap((value) => {
                // if not in the localStorage, call the API
                if (!value && !configLoadedFromAPI) {
                    configLoadedFromAPI = true;
                    return this._fetchMapsConfigFromAPI$();
                } else {
                    return of(value);
                }
            }),
        ),
        appDataLoaded$.pipe(
            filter(loaded => loaded),
            take(1),
            filter(() => !configLoadedFromAPI),
            switchMap(() => {
                configLoadedFromAPI = true;
                return this._fetchMapsConfigFromAPI$();
            } )
        )
    ).toPromise();

    this.maps = { satellite: maps.satellite, openStreetMap: maps.openStreetMap };
    const mapToUse = this.maps[mapConfig?.mapType] ?? this.maps.openStreetMap;
    const openStreetMap = await arcgisUtils.createMap(
      mapToUse,
      mapViewEl.nativeElement,
      {
        mapOptions: {
          showLabels: true,
          minZoom: 2,
          maxZoom: 22
        }
      }
    );

    this._mapViewEl = mapViewEl;
    this.webMap = openStreetMap.map;

    this._beaconsVisible = !!mapConfig?.displayBeacons;
    this._anchorsVisible = !!mapConfig?.displayAnchors;
    this.zoneDisplayed = !!mapConfig?.displayZone;
    this._assetLabelsVisible = !!mapConfig?.displayAssetLabels;
    this._disabledMovementStatus = !!mapConfig?.disabledMovementStatus;
    this._disabledOutdatedHorizon = mapConfig?.disabledOutdatedHorizon ?? false;
    // hide indoor map by default
    this.setIndoorLayerVisibility(!!mapConfig?.displayIndoor);
    this._indoorVisible = !!mapConfig?.displayIndoor;
    this._initWebMapConfig();
  }

  private _fetchMapsConfigFromAPI$() {
    return this.esriQueryService.getEsriMap().pipe(
      tap(
        mapData => this._localStorageService.store$(
          ConfigurationStorageKeys.mapData,
          JSON.stringify(mapData)
        ).subscribe())
    );
  }

  /**
   * Display assets pinpoints and clusters
   */
  public async displayAssets(assets: Asset[]) {
    // create cluster layer
    const assetClusterLayer = this._createAssetClustering(assets);
    // Create graphic layer (pinpoints)
    const assetGraphicsLayer = this._createAssetGraphicsLayer(assetClusterLayer);
    // Add popin on graphic
    this._addPopinOnGraphicLayer(assetGraphicsLayer, 'asset', 'popin-element-cluster');
    this._addPopinOnClusterLayer(assetClusterLayer, 'popin-element-cluster');
    const outerPointer = document.querySelector('.outerPointer');
    if (outerPointer) {
      outerPointer.remove();
    }
  }

  /**
   *  Create the asset cluster layer
   */
  private _createAssetClustering(assets: Asset[]) {
    const allPoints = [];

    for (const asset of assets) {
      if (asset.last_event && asset.last_event.lng && asset.last_event.lat && ((asset.tracker &&
        Object.keys(asset.tracker).length) || (asset.last_event.source === SignalSources.GO2SCAN))) {
        const point = new Point({ longitude: asset.last_event.lng, latitude: asset.last_event.lat });
        const coord = webMercatorUtils.geographicToWebMercator(point);
        allPoints.push({
          x: coord.x,
          y: coord.y,
          attributes: { ...asset, ...{ type: 'asset' } }
        });
      }
    }
    const popupTemplate = new PopupTemplate({});

    const assetClusterFeatureLayer = new ClusterFeatureLayer({
      'data': allPoints,
      'distance': 50,
      'labelColor': '#fff',
      'singleTemplate': popupTemplate,
      'showSingles': false
    });

    assetClusterFeatureLayer['customId'] = 'assetClusterFeatureLayer';

    const defaultSym = new Symbol();
    const [small, medium, large, xlarge] = this.getMarkersAsset([50, 75, 90, 110]);
    const renderer = new ClassBreaksRenderer(defaultSym, 'clusterCount');
    // Break values - can adjust easily
    renderer.addBreak(2, 50, small);
    renderer.addBreak(50, 250, medium);
    renderer.addBreak(250, 1000, large);
    renderer.addBreak(1000, 50000, xlarge);
    // Providing a ClassBreakRenderer is also optional
    assetClusterFeatureLayer.setRenderer(renderer);
    if (this._getLayer('assetClusterFeatureLayer')) {
      this._getLayer('assetClusterFeatureLayer')['toRemove'] = true;
    }

    this.webMap.addLayer(assetClusterFeatureLayer, 1);
    // Refresh else the cluster do not display on pan end
    assetClusterFeatureLayer.refresh();
    // the old layer deletion is handled by the layer-add event in order to avoid blinking

    return assetClusterFeatureLayer;
  }

  /**
   * Used to get layer by id or by customid for assetClusterFeatureLayer
   */
  _getLayer(id: string): Layer {
    if (id === 'assetClusterFeatureLayer') {
      return this.webMap.getLayersVisibleAtScale().find((layer: any) => layer['customId'] === id && !layer['toRemove']);
    } else {
      return this.webMap.getLayer(id);
    }
  }

  /**
   * Remove layers flaged for deletion
   */
  _removeFlagedLayers() {
    const toRemove = this.webMap.getLayersVisibleAtScale().filter((layer: any) => layer['toRemove']);
    this.removeLayer(toRemove);
  }

  /**
   * Create the asset pin point layer (graphic)
   */
  private _createAssetGraphicsLayer(assetClusterFeatureLayer: any) {
    // return new Promise((resolve, reject) => {

    const assetGraphicsLayer = new GraphicsLayer({
      id: 'assetGraphicsLayer',
      infoTemplate: new PopupTemplate({})
    });
    this.removeLayer([this.webMap.getLayer('assetGraphicsLayer')]);
    this.webMap.addLayer(assetGraphicsLayer, 5);
    this._checkMinOrMaxZoom();
    // Get layer for the labels
    const assetLabelsGraphicsLayer = this._createAssetLabelsGraphicLayer();

    if (assetClusterFeatureLayer) {
      for (const cluster of assetClusterFeatureLayer.graphics) {
        const clusterId = cluster.attributes['clusterId'];
        // This means that the asset is not in a cluster and need a pin display
        if (cluster.attributes['clusterCount'] === 1) {
          // clusterId = cluster.attributes['clusterId'];
          for (const _clusterData of assetClusterFeatureLayer._clusterData) {
            if (_clusterData.attributes.type !== 'asset' || _clusterData.attributes['clusterId'] !== clusterId) {
              continue;
            }

            const point = new Point(_clusterData.x, _clusterData.y, new SpatialReference({ wkid: 102100 }));

            this._addAssetToGraphicLayer(assetGraphicsLayer, point, _clusterData.attributes);
            if (assetLabelsGraphicsLayer) {
              this._addAssetLabelToGraphicLayer(assetLabelsGraphicsLayer, point, _clusterData.attributes);
            }
          }
        } else { // asset pinpoint is not rendered but we need the point on graphic layer in order to display popins
          if (this.webMap.getZoom() === this.webMap.getMaxZoom()) {
            for (const _cluster of assetClusterFeatureLayer._clusterData) {
              const attributes = _cluster.attributes;
              if (attributes.type !== 'asset' || attributes['clusterId'] !== clusterId) {
                continue;
              }
              const point = new Point(_cluster.x, _cluster.y, new SpatialReference({ wkid: 102100 }));
              const graphic = new Graphic(point, new Symbol(), attributes);
              assetGraphicsLayer.add(graphic);
            }
          }
        }
      }
    }

    return assetGraphicsLayer;
  }

  /**
   * Add the asset to its graphic layer
   * @param layer The layer for the pinpoints of the assets
   * @param point The point on the map
   * @param attributes The attributes of the asset
   */
  private _addAssetToGraphicLayer(layer: GraphicsLayerConstructor, point: PointConstructor, attributes: Asset) {
    let backgroundColor = this._mapScopeService.getBackGroundColor(attributes, this._disabledMovementStatus);
    let markerColor = this._mapScopeService.getPictoColor(attributes, this._disabledMovementStatus);
    let isOutlined = this._mapScopeService.isPictoOutlined(attributes);
    let outlineColor = this._mapScopeService.getOutlineColor(attributes, this._disabledMovementStatus);
    let shouldRenderBackground = true;

    if (this._isLowAccuracy(attributes)) {
      backgroundColor = [...backgroundColor, this.STALE_PINPOINT_ALPHA];
      markerColor = [...markerColor, this.STALE_PINPOINT_ALPHA];
      shouldRenderBackground = false;
    }
    // Keep stale rendering consistent for both empty and loaded assets.
   if (
    !this._disabledOutdatedHorizon &&
    this._isAssetOutdated(attributes)
  ) {
    backgroundColor = [
        ...PICTO_COLOR.white,
        this.STALE_PINPOINT_ALPHA,
    ];

    markerColor = [
        ...PICTO_COLOR.grey,
        this.STALE_PINPOINT_ALPHA,
    ];

    isOutlined = false;
    outlineColor = null;
    shouldRenderBackground = false;
    }
    const backgroundPicto = this._createSymbol(
      this._mapScopeService.getBackGroundPicto(),
      backgroundColor,
      false,
      null
    );
    const markerPicto = this._createSymbol(
      this._mapScopeService.getPictoByAsset(attributes),
      markerColor,
      isOutlined,
      outlineColor,
    );

    const marker = new Graphic(point, markerPicto, attributes);
    if (shouldRenderBackground) {
      const background = new Graphic(point, backgroundPicto, attributes);
      layer.add(background);
    }
    layer.add(marker);
  }

  /**
      * Check if the asset's last_event.event_time or tracker.keepAlive is older than configured horizon
     * @param asset The asset to check
      * @returns true if date is older than configured provider horizon
     */
  private _isAssetOutdated(asset: Asset): boolean {
    const eventTime = asset.last_event?.event_time ? new Date(asset.last_event.event_time) : null;
    const keepAlive = asset.tracker?.keepAlive ? new Date(asset.tracker.keepAlive) : null;

    const eventTimestamp = eventTime && !isNaN(eventTime.getTime()) ? eventTime.getTime() : null;
    const keepAliveTimestamp = keepAlive && !isNaN(keepAlive.getTime()) ? keepAlive.getTime() : null;
    const mostRecentTimestamp = Math.max(eventTimestamp ?? 0, keepAliveTimestamp ?? 0);

    if (!mostRecentTimestamp) {
      return false;
    }

    const outdatedThresholdMs = this._outdatedHorizonService.getOutdatedThresholdMs(asset.tracker?.provider);
    const thresholdTimestamp = Date.now() - outdatedThresholdMs;

    return mostRecentTimestamp < thresholdTimestamp;
  }

  /**
   * Check if the asset's last_event.accuracy is low
   * @param asset The asset to check
   * @returns true if accuracy is low
   */
  private _isLowAccuracy(asset: Asset): boolean {

    const accuracy = asset.last_event?.accuracy ? asset.last_event?.accuracy : null;

    if (!accuracy) {
      return false;
    }
    if (asset.last_event?.source === 'Anchor' && asset.tracker?.provider === 'A-Swarm') {
      return false;
    }
    return accuracy > 20;
  }

  /**
   * Add the label for the asset on its layer
   * @param layer The graphic layer for the labels of the assets
   * @param point The point on the map
   * @param attributes The attributes of the asset
   */
  private _addAssetLabelToGraphicLayer(layer: GraphicsLayerConstructor, point: PointConstructor, attributes: Asset) {
    const { fontFamily, fontSize } = window.getComputedStyle(this.webMap.root as Element);
    let fontHeight = parseInt(fontSize, 10);
    fontHeight = fontHeight % 2 ? fontHeight + 1 : fontHeight;

    // Create bullet (background)
    const bulletSymbol = this._createSymbol(
      this._generateAssetLabelSvg(attributes?.name, fontHeight, fontFamily, 4),
      PICTO_COLOR.black.concat([0.8]),
      false,
      null,
    );
    const bulletSize = this._getTextWidth(attributes?.name, fontFamily, fontHeight) + 2 + fontHeight;
    bulletSymbol.setSize(bulletSize + 2);
    bulletSymbol.setOffset((bulletSize / 2) + 1, 0);
    const bullet = new Graphic(point, bulletSymbol, attributes);

    // Create text
    const textSymbol = new TextSymbol(attributes?.name);
    textSymbol.setColor(new Color(PICTO_COLOR.white));
    textSymbol.font.setSize(fontSize);
    textSymbol.font.setFamily(fontFamily);
    textSymbol.setOffset((bulletSize / 2) + 6, -4);
    const text = new Graphic(point, textSymbol, attributes);

    // Add to the layer
    layer.add(bullet);
    layer.add(text);
  }


  /**
   * Create the layer for the labels of the assets
   */
  private _createAssetLabelsGraphicLayer() {
    const assetLabelsGraphicsLayer = new GraphicsLayer({
      id: 'assetLabelsGraphicsLayer',
      infoTemplate: new PopupTemplate({}),
      visible: this._assetLabelsVisible,
    });
    this.removeLayer([this.webMap.getLayer('assetLabelsGraphicsLayer')]);
    this.webMap.addLayer(assetLabelsGraphicsLayer, 4);
    return assetLabelsGraphicsLayer;
  }

  /**
   *
   * @param assetName The name of the asset
   * @param fontHeight The height of the font (default = `16`)
   * @param fontFamily The font family (default = `'Roboto'`)
   * @param padding The padding around the font height (default = `1`)
   * @returns The svg path value
   */
  private _generateAssetLabelSvg(assetName: string, fontHeight = 16, fontFamily = 'Inter, Roboto', padding = 1): string {
    // Ensure the font height is a multiple of 2
    if (fontHeight % 2) {
      fontHeight += 1;
    }
    const textWidth = this._getTextWidth(assetName, fontFamily) + fontHeight;
    const bulletHeight = fontHeight + padding;
    const endRadius = 4;
    // Result example: M0,10 L6,4 S10,0 14,0 l66,0 q4,0 4,4 l0,12 q0,4 -4,4 l-66,0 S10,20 6,16 L0,10
    const svgSteps = [
      `M0,${bulletHeight / 2}`,
      `L${(bulletHeight / 2) - padding},${padding}`,
      `S${(bulletHeight / 2)},0 ${(bulletHeight / 2) + padding},0`,
      `l${textWidth},0`,
      `q${endRadius},0 ${endRadius},${endRadius}`,
      `l0,${bulletHeight - (2 * endRadius)}`,
      `q0,${endRadius} -${endRadius},${endRadius}`,
      `l${-textWidth},0`,
      `S${bulletHeight / 2},${bulletHeight} ${(bulletHeight / 2) - padding},${bulletHeight - padding}`,
      `L0,${bulletHeight / 2}`,
    ];
    return svgSteps.join(' ');
  }

  /**
   * Compute the width of the text
   * @param text The text to compute the width
   * @param fontFamily The font family
   * @param fontSize The font size
   * @returns The width of the text
   */
  private _getTextWidth(text: string, fontFamily: string, fontSize?: number): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = `${fontSize ? `${fontSize}px` : ''} ${fontFamily}`;
    const metrics = context.measureText(text);
    return Math.ceil(metrics.width);
  }

  /**
   * create the zone layer
   * - visibility should be adjusted thru setZonesVisibility methode
   */
  public async displayZones(zones: Zone[]) {
    this.zones = zones;
    this.removeLayer([this.webMap.getLayer('zone')]);
    const zoneGraphicLayer = this.createZonesLayer(this.zoneDisplayed || false);
    // Refresh else the cluster do not display on pan end
    // The method exists in the class but it not correctly typed
    (zoneGraphicLayer as any).refresh();
    this._addPopinOnGraphicLayer(zoneGraphicLayer, 'zone', 'popin-element-zone');
  }

  /**
   * Zoom on zones and center
   */
  public async zoomAndCenterOnZones(zones?: Zone[]) {
    if (zones) { this.zones = zones; }
    if (this.zoneDisplayed) {
      if (this.zones.length) {
        // retrieve lat and long inside a flat array
        const allLat = this.zones
          .map(zone => zone.coordinates.coordinates[0].map(coord => coord[1]))
          .reduce((acc, current) => acc.concat(current[0]));

        const allLong = this.zones
          .map(zone => zone.coordinates.coordinates[0].map(coord => coord[0]))
          .reduce((acc, current) => acc.concat(current[0]));
        // calculate max and min
        const latMin = Math.min(...allLat);
        const latMax = Math.max(...allLat);
        const lngMin = Math.min(...allLong);
        const lngMax = Math.max(...allLong);

        this.webMap.setExtent(new Extent(lngMin, latMin, lngMax, latMax, new SpatialReference({ wkid: 4326 })), true);
      }
    }
  }

  private _refreshLayers(assets: Asset[], beacons$: Observable<EsriBeacon[]>, extent: ExtentConstructor, anchors$: Observable<Anchor[]>) {
    this._applyIndoorVisibility();

    const assetsInBox = this._filterAssetsInBox(assets, extent);
    if (this._getLayer('assetClusterFeatureLayer')?.visible) {
      this.displayAssets(assetsInBox);
      this._removeFlagedLayers();
    }

    if (this.webMap.getLayer('beaconGraphicsLayer')?.visible) {
      beacons$.pipe(
        take(1),
        map(beacons => beacons.sort(
          (a, b) => (+a.geometry?.x || 0) - (+b.geometry?.x || 0)
        )),
      ).subscribe(beacons => {
        const beaconsInBox = this._filterBeaconsInBox(beacons, extent);
        this.displayBeacons(beaconsInBox);
      });
    }

    if (this.webMap.getLayer('anchorGraphicsLayer')?.visible) {
      anchors$.pipe(
        take(1),
        map(anchors => anchors.sort(
          (a, b) => (+a.longitude || 0) - (+b.longitude || 0)
        )),
      ).subscribe(anchors => {
        this.displayAnchors(anchors);
      });
    }

    // handle history refresh on zoom
    if (this.webMap.getLayer('historyAssetClusterFeatureLayer')?.visible) {
      this.displayHistory(this._lastHistory, this._lastAssetForHistory);
    }

    if (this.webMap.getLayer('zone')?.visible) {
      this.displayZones(this.zones);
    }
  }

  /**
   * Subscribe on zoom-end event and pan-end and rerender the visible layers
   */
  public refreshLayers(assets: Asset[], beacons$: Observable<EsriBeacon[]>, anchor$: Observable<Anchor[]>) {
    // Sort assets by longitude for later filtering in viewport
    assets.sort(
      (a, b) => (+a.last_event?.lng || 0) - (+b.last_event?.lng || 0)
    );
    this.webMap.onPanEnd = (event: any) => {
      this._refreshLayers(assets, beacons$, event, anchor$);
    };
    this.webMap.onZoomEnd = (event: any) => {
      this._refreshLayers(assets, beacons$, event, anchor$);
    };
    this.webMap.graphicsLayerIds.forEach(
      (id: any) => this.webMap.getLayer(id)?.on('visibility-change', (event: any) => {
        if (event.visible) {
          this._refreshLayers(assets, beacons$, this.webMap.extent, anchor$);
        }
      })
    );
    this.webMap.on('layer-add', ({ layer }: any) => {
      layer.on('visibility-change', (event: any) => {
        if (event.visible) {
          this._refreshLayers(assets, beacons$, this.webMap.extent, anchor$);
        }
      });
    });
  }

  /**
   * latitude = y
   * longitude = x
   */
  private _filterBeaconsInBox(beacons: EsriBeacon[], extent: ExtentConstructor): EsriBeacon[] {
    const [xmin, ymin] = webMercatorUtils.xyToLngLat(extent.xmin, extent.ymin);
    const [xmax, ymax] = webMercatorUtils.xyToLngLat(extent.xmax, extent.ymax);
    // Assets are sorted by longitude
    let filteredBeacons: EsriBeacon[] = beacons;
    // Longitude
    if (xmin <= xmax) {
      filteredBeacons = this._filterCoordsInside<EsriBeacon>(beacons, xmin, xmax, ['geometry', 'x']);
    } else {
      filteredBeacons = this._filterCoordsOutside<EsriBeacon>(beacons, xmax, xmin, ['geometry', 'x']);
    }
    // Latitude
    if (ymin <= ymax) {
      filteredBeacons = this._filterCoordsInside<EsriBeacon>(filteredBeacons, ymin, ymax, ['geometry', 'y']);
    } else {
      filteredBeacons = this._filterCoordsOutside<EsriBeacon>(filteredBeacons, ymax, ymin, ['geometry', 'y']);
    }
    return filteredBeacons;
  }

  /**
   * Get the value of the property given by the path
   * @param item The item
   * @param propertyPath The path to the property
   * @returns The property under the path
   */
  private _getValueFromPropertyPath<T, R>(item: T, propertyPath: string[]): R {
    let value = item;
    for (const property of propertyPath) {
      value = value?.[property];
    }
    return value as unknown as R;
  }

  /**
   * Filter the items by the coordinates which are inside the two coordinates
   * @param items The items to filter
   * @param min The minimum coordinate of the axis
   * @param max The maximum coordinate of the axis
   * @param propertyPath The path to the coordinate property to filter on
   * @description
   * ### Explanation
   * On the horizontal axis, will select only the `O` and not the `X`
   * ```
   *    map                        map
   *    min    min         max     max
   *     ----------------------------
   *     |      -------------       |
   *     |  X   |     O     | X   X |
   *     |   X  |   O     O |     X |
   *     |      -------------       |
   *     ----------------------------
   * ```
   */
  private _filterCoordsInside<T>(items: T[], min: number, max: number, propertyPath: string[]): T[] {
    let filteredItems: T[] = cloneDeep(items);
    filteredItems.sort(
      (a, b) => +this._getValueFromPropertyPath(a, propertyPath) - +this._getValueFromPropertyPath(b, propertyPath)
    );
    const firstIndex = filteredItems.findIndex(
      item => +this._getValueFromPropertyPath(item, propertyPath) >= min
    );
    if (firstIndex > -1) {
      filteredItems = filteredItems.slice(firstIndex);
    }
    // Find last asset in the box
    const lastIndex = filteredItems.findIndex(
      item => +this._getValueFromPropertyPath(item, propertyPath) > max
    );
    if (lastIndex > -1) {
      filteredItems = filteredItems.slice(0, lastIndex);
    }
    return filteredItems;
  }

  /**
   * Filter the items by the coordinates which are outside the two coordinates (<= min & >= max)
   * @param items The items to filter
   * @param min The minimum coordinate of the axis
   * @param max The maximum coordinate of the axis
   * @param propertyPath The path to the coordinate property to filter on
   * @description
   * ### Explanation
   * On the horizontal axis, will select only the `O` and not the `X`
   * ```
   *    map                        map
   *    min    min         max     max
   *     ----------------------------
   *  ---+-------           --------+----
   *     |  O   |     X     | O   O |
   *     |   O  |   X     X |     O |
   *  ---+-------           --------+----
   *     ----------------------------
   * ```
   * Even if it's "inside" a box on the screen, it's "outside" of min/max coordinates box on the given axis
   */
  private _filterCoordsOutside<T>(items: T[], min: number, max: number, propertyPath: string[]): T[] {
    const itemsCopy = cloneDeep(items);
    itemsCopy.sort(
      (a, b) => +this._getValueFromPropertyPath(a, propertyPath) - +this._getValueFromPropertyPath(b, propertyPath)
    );
    const filteredItems: T[] = [];
    // Find first item outside of the west side of the box
    const firstIndex = itemsCopy.findIndex(
      item => +this._getValueFromPropertyPath(item, propertyPath) > min
    );
    // Take the items at the west/south (< min)
    filteredItems.push(...itemsCopy.splice(0, firstIndex));
    // Find first item inside the east side of the box
    let lastIndex = itemsCopy.findIndex(
      item => +this._getValueFromPropertyPath(item, propertyPath) >= max
    );
    if (lastIndex === -1 && min * max > 0) {
      lastIndex = 0;
    }
    if (lastIndex > -1) {
      // Take the items at the east/north
      filteredItems.push(...itemsCopy.splice(lastIndex, itemsCopy.length));
    }
    return filteredItems;
  }

  /**
   * latitude = y
   * longitude = x
   */
  private _filterAssetsInBox(assets: Asset[], extent: ExtentConstructor): Asset[] {
    const [xmin, ymin] = webMercatorUtils.xyToLngLat(extent.xmin, extent.ymin);
    const [xmax, ymax] = webMercatorUtils.xyToLngLat(extent.xmax, extent.ymax);
    // Assets are sorted by longitude
    let filteredAssets: Asset[] = assets;
    // Longitude
    if (xmin <= xmax) {
      filteredAssets = this._filterCoordsInside<Asset>(assets, xmin, xmax, ['last_event', 'lng']);
    } else {
      filteredAssets = this._filterCoordsOutside<Asset>(assets, xmax, xmin, ['last_event', 'lng']);
    }
    // Latitude
    if (ymin <= ymax) {
      filteredAssets = this._filterCoordsInside<Asset>(filteredAssets, ymin, ymax, ['last_event', 'lat']);
    } else {
      filteredAssets = this._filterCoordsOutside<Asset>(filteredAssets, ymax, ymin, ['last_event', 'lat']);
    }
    return filteredAssets;
  }


  private createZonesLayer(visible: boolean) {
    const zoneGraphicsLayer = new GraphicsLayer({
      id: 'zone',
      visible: visible,
      infoTemplate: new PopupTemplate({})
    });

    for (const item of this.zones) {
      const polygon = new Polygon(new SpatialReference({ wkid: 4326 }));

      polygon.addRing(item.coordinates.coordinates[0]);
      const poly_wm = webMercatorUtils.geographicToWebMercator(polygon);

      // Define the border of the zone
      const color = this._mapScopeService.getZoneColor(item);
      const outline = new SimpleLineSymbol();

      outline.setColor(new Color(color.border));

      // Define the zone content
      const symbol = new SimpleFillSymbol();
      symbol.setColor(new Color(color.background));
      symbol.setOutline(outline);
      const graphic = new Graphic(poly_wm, symbol, item);
      zoneGraphicsLayer.add(graphic);

      const point = graphic.geometry.getExtent().getCenter();

      if (this.webMap.getZoom() >= 13) {
        const zonesLabel = new TextSymbol(item.name);
        zonesLabel.setColor(new Color('#00205B'));
        zonesLabel.font.setSize('12pt');
        zonesLabel.font.setFamily('Inter, Roboto');
        const graphicLabel = new Graphic(point, zonesLabel, item);
        zoneGraphicsLayer.add(graphicLabel);
      }
    }
    this.webMap.addLayer(zoneGraphicsLayer, 0);
    return zoneGraphicsLayer;
  }

  /**
   * Create the beacon layer
   */
  public async displayBeacons(beacons: EsriBeacon[]) {
    this.removeLayer([this.webMap.getLayer('beaconClusterFeatureLayer'), this.webMap.getLayer('beaconGraphicsLayer')]);
    const beaconClusterLayer = this._createBeaconClustering(beacons);
    const beaconGraphicLayer = this._createBeaconLayer(beaconClusterLayer);
    this._addPopinOnGraphicLayer(beaconGraphicLayer, 'beacon', 'popin-element-beacon-cluster');
    this._addPopinOnClusterLayer(beaconClusterLayer, 'popin-element-beacon-cluster');
  }

  /**
   * Create the beacons clusters layer
   */
  private _createBeaconClustering(beacons: EsriBeacon[]) {
    // Load the modules for the ArcGIS API for JavaScript
    const allpoints = [];

    if (beacons) {
      for (const beacon of beacons) {
        const point = new Point(+beacon.geometry.x, +beacon.geometry.y);
        allpoints.push({
          x: webMercatorUtils.geographicToWebMercator(point).x,
          y: webMercatorUtils.geographicToWebMercator(point).y,
          attributes: { ...beacon.attributes, ...{ type: 'beacon' } }
        });
      }

      const popupTemplate = new PopupTemplate({});

      const beaconClusterFeatureLayer = new ClusterFeatureLayer({
        'data': allpoints,
        'distance': 50,
        'id': 'beaconClusterFeatureLayer',
        'labelColor': '#fff',
        'singleTemplate': popupTemplate,
        'showSingles': false,
        'visible': this._beaconsVisible
      });

      const defaultSym = new Symbol();
      const [small, medium, large, xlarge] = this.getMarkersBeacon([50, 75, 90, 110]);
      const renderer = new ClassBreaksRenderer(defaultSym, 'clusterCount');

      // Break values - can adjust easily
      renderer.addBreak(2, 50, small);
      renderer.addBreak(50, 250, medium);
      renderer.addBreak(250, 1000, large);
      renderer.addBreak(1000, 50000, xlarge);

      // Providing a ClassBreakRenderer is also optional
      beaconClusterFeatureLayer.setRenderer(renderer);
      this.webMap.addLayer(beaconClusterFeatureLayer, 3);
      // Refresh else the cluster do not display on pan end
      beaconClusterFeatureLayer.refresh();
      return beaconClusterFeatureLayer;
    }
  }

  /**
     * Create the layer for the labelsof the beacons
   */
  private _createBeaconLabelsGraphicLayer() {
    const beaconLabelsGraphicsLayer = new GraphicsLayer({
      id: 'beaconLabelsGraphicsLayer',
      infoTemplate: new PopupTemplate({}),
      visible: this._beaconsVisible,
    });
    this.removeLayer([this.webMap.getLayer('beaconLabelsGraphicsLayer')]);
    this.webMap.addLayer(beaconLabelsGraphicsLayer, 1);
    return beaconLabelsGraphicsLayer;
  }

  /**
    * Add the label for the Beacon on its layer
    * @param layer The graphic layer for the labels of the Beacon
    * @param point The point on the map
    * @param attributes The attributes of the Beacon
    */
  private _addBeaconLabelToGraphicLayer(layer: GraphicsLayerConstructor, point: PointConstructor, attributes: Beacon) {
    if (!layer) return
    const { fontFamily, fontSize } = window.getComputedStyle(this.webMap.root as Element);
    let fontHeight = parseInt(fontSize, 10);
    fontHeight = fontHeight % 2 ? fontHeight + 1 : fontHeight;

    // Create bullet (background)
    const bulletSymbol = this._createSymbol(
      this._generateAssetLabelSvg(attributes?.name, fontHeight, fontFamily, 4),
      PICTO_COLOR.black.concat([0.8]),
      false,
      null,
    );
    const bulletSize = this._getTextWidth(attributes?.name, fontFamily, fontHeight) + 2 + fontHeight;
    bulletSymbol.setSize(bulletSize + 2);
    bulletSymbol.setOffset((bulletSize / 2) + 1, 0);
    const bullet = new Graphic(point, bulletSymbol, attributes);

    // Create text
    const textSymbol = new TextSymbol(attributes?.name);
    textSymbol.setColor(new Color(PICTO_COLOR.white));
    textSymbol.font.setSize(fontSize);
    textSymbol.font.setFamily(fontFamily);
    textSymbol.setOffset((bulletSize / 2) + 6, -4);
    const text = new Graphic(point, textSymbol, attributes);

    // Add to the layer
    layer.add(bullet);
    layer.add(text);
  }


  /**
   * Create the beacon pin point layer
   */
  private _createBeaconLayer(beaconClusterFeatureLayer: GraphicsLayerConstructor & { _clusterData: any[] }) {
    const beaconGraphicsLayer = new GraphicsLayer({
      id: 'beaconGraphicsLayer',
      infoTemplate: new PopupTemplate({}),
      visible: this._beaconsVisible
    });

    const beaconLabelsGraphicsLayer = this._createBeaconLabelsGraphicLayer()

    if (beaconClusterFeatureLayer) {
      for (const cluster of beaconClusterFeatureLayer.graphics) {
        const clusterId = cluster.attributes['clusterId'];
        // This means that the beacon is not in a cluster
        if (cluster.attributes['clusterCount'] === 1) {
          for (const feature of beaconClusterFeatureLayer._clusterData) {
            const attributes = feature.attributes;
            if (attributes.type !== 'beacon' || attributes['clusterId'] !== clusterId) {
              continue;
            }
            const point = new Point(feature.x, feature.y, new SpatialReference({ wkid: 102100 }));
            this._addBeaconLabelToGraphicLayer(beaconLabelsGraphicsLayer, point, feature.attributes);
            const background = new Graphic(point, this._createSymbol(this._backgroundSVG, this.white, false, null), attributes);
            const marker = this._createSymbol(this._baseBeaconSVG, this.blue, false, null);
            const graphic = new Graphic(point, marker, attributes);
            beaconGraphicsLayer.add(background);
            beaconGraphicsLayer.add(graphic);
          }
        } else {
          if (this.webMap.getZoom() === this.webMap.getMaxZoom()) {
            for (const feature of beaconClusterFeatureLayer._clusterData) {
              const attributes = feature.attributes;
              if (attributes.type !== 'beacon' || attributes['clusterId'] !== clusterId) {
                continue;
              }
              const point = new Point(feature.x, feature.y, new SpatialReference({ wkid: 102100 }));
              const graphic = new Graphic(point, new Symbol(), attributes);
              beaconGraphicsLayer.add(graphic);
            }
          }
        }
      }
    }

    this.webMap.addLayer(beaconGraphicsLayer, 2);
    (beaconGraphicsLayer as any).refresh();
    return beaconGraphicsLayer;
  }


  /**
  * Create the beacon layer
  */
  public async displayAnchors(anchors: Anchor[]) {
    this.removeLayer([this.webMap.getLayer('anchorClusterFeatureLayer'), this.webMap.getLayer('anchorGraphicsLayer')]);
    const anchorClusterLayer = this._createAnchorClustering(anchors);
    const anchorGraphicLayer = this._createAnchorLayer(anchorClusterLayer);
    this._addPopinOnGraphicLayer(anchorGraphicLayer, 'anchor', 'popin-element-anchor-cluster');
    this._addPopinOnClusterLayer(anchorClusterLayer, 'popin-element-anchor-cluster');
  }

  /**
   * Create the beacons clusters layer
   */
  private _createAnchorClustering(anchors: Anchor[]) {
    // Load the modules for the ArcGIS API for JavaScript
    const allpoints = [];

    if (anchors) {
      for (const anchor of anchors) {
        const point = new Point(+anchor.longitude, +anchor.latitude);
        allpoints.push({
          x: webMercatorUtils.geographicToWebMercator(point).x,
          y: webMercatorUtils.geographicToWebMercator(point).y,
          attributes: { ...anchor, ...{ type: 'anchor' } }
        });
      }

      const popupTemplate = new PopupTemplate({});

      const anchorClusterFeatureLayer = new ClusterFeatureLayer({
        'data': allpoints,
        'distance': 50,
        'id': 'anchorClusterFeatureLayer',
        'labelColor': '#fff',
        'singleTemplate': popupTemplate,
        'showSingles': false,
        'visible': this._anchorsVisible
      });

      const defaultSym = new Symbol();
      const [small, medium, large, xlarge] = this.getMarkersAnchor([50, 75, 90, 110]);
      const renderer = new ClassBreaksRenderer(defaultSym, 'clusterCount');

      // Break values - can adjust easily
      renderer.addBreak(2, 50, small);
      renderer.addBreak(50, 250, medium);
      renderer.addBreak(250, 1000, large);
      renderer.addBreak(1000, 50000, xlarge);

      // Providing a ClassBreakRenderer is also optional
      anchorClusterFeatureLayer.setRenderer(renderer);
      this.webMap.addLayer(anchorClusterFeatureLayer, 4);
      // Refresh else the cluster do not display on pan end
      anchorClusterFeatureLayer.refresh();
      return anchorClusterFeatureLayer;
    }
  }

  /**
  * Create the layer for the labels of the anchors
  */
  private _createAnchorLabelsGraphicLayer() {
    const anchorLabelsGraphicsLayer = new GraphicsLayer({
      id: 'anchorLabelsGraphicsLayer',
      infoTemplate: new PopupTemplate({}),
      visible: this._anchorsVisible,
    });
    this.removeLayer([this.webMap.getLayer('anchorLabelsGraphicsLayer')]);
    this.webMap.addLayer(anchorLabelsGraphicsLayer, 2);
    return anchorLabelsGraphicsLayer;
  }

  /**
  * Add the label for the Anchor on its layer
  * @param layer The graphic layer for the labels of the Anchor
  * @param point The point on the map
  * @param attributes The attributes of the Anchor
  */
  private _addAnchorLabelToGraphicLayer(layer: GraphicsLayerConstructor, point: PointConstructor, attributes: Anchor) {
    if (!layer) return
    const { fontFamily, fontSize } = window.getComputedStyle(this.webMap.root as Element);
    let fontHeight = parseInt(fontSize, 10);
    fontHeight = fontHeight % 2 ? fontHeight + 1 : fontHeight;

    // Create bullet (background)
    const bulletSymbol = this._createSymbol(
      this._generateAssetLabelSvg(attributes?.anchorId, fontHeight, fontFamily, 4),
      PICTO_COLOR.black.concat([0.8]),
      false,
      null,
    );
    const bulletSize = this._getTextWidth(attributes?.anchorId, fontFamily, fontHeight) + 2 + fontHeight;
    bulletSymbol.setSize(bulletSize + 2);
    bulletSymbol.setOffset((bulletSize / 2) + 1, 0);
    const bullet = new Graphic(point, bulletSymbol, attributes);

    // Create text
    const textSymbol = new TextSymbol(attributes?.anchorId);
    textSymbol.setColor(new Color(PICTO_COLOR.white));
    textSymbol.font.setSize(fontSize);
    textSymbol.font.setFamily(fontFamily);
    textSymbol.setOffset((bulletSize / 2) + 6, -4);
    const text = new Graphic(point, textSymbol, attributes);

    // Add to the layer
    layer.add(bullet);
    layer.add(text);
  }

  /**
   * Create the a anchor pin point layer
   */
  private _createAnchorLayer(anchorClusterFeatureLayer: GraphicsLayerConstructor & { _clusterData: any[] }) {
    const anchorGraphicsLayer = new GraphicsLayer({
      id: 'anchorGraphicsLayer',
      infoTemplate: new PopupTemplate({}),
      visible: this._anchorsVisible
    });

    const anchorLabelGraphicLayer = this._createAnchorLabelsGraphicLayer()

    if (anchorClusterFeatureLayer) {
      for (const cluster of anchorClusterFeatureLayer.graphics) {
        const clusterId = cluster.attributes['clusterId'];
        // This means that the anchor is not in a cluster
        if (cluster.attributes['clusterCount'] === 1) {
          for (const feature of anchorClusterFeatureLayer._clusterData) {
            const attributes = feature.attributes;
            if (attributes.type !== 'anchor' || attributes['clusterId'] !== clusterId) {
              continue;
            }
            const point = new Point(feature.x, feature.y, new SpatialReference({ wkid: 102100 }));
            this._addAnchorLabelToGraphicLayer(anchorLabelGraphicLayer, point, feature.attributes);
            const background = new Graphic(point, this._createSymbol(this._backgroundSVG, this.white, false, null), attributes);
            const marker = this._createSymbol(this._baseBeaconSVG, this.gray, false, null);
            const graphic = new Graphic(point, marker, attributes);
            anchorGraphicsLayer.add(background);
            anchorGraphicsLayer.add(graphic);
          }
        } else {
          if (this.webMap.getZoom() === this.webMap.getMaxZoom()) {
            for (const feature of anchorClusterFeatureLayer._clusterData) {
              const attributes = feature.attributes;
              if (attributes.type !== 'anchor' || attributes['clusterId'] !== clusterId) {
                continue;
              }
              const point = new Point(feature.x, feature.y, new SpatialReference({ wkid: 102100 }));
              const graphic = new Graphic(point, new Symbol(), attributes);
              anchorGraphicsLayer.add(graphic);
            }
          }
        }
      }
    }

    this.webMap.addLayer(anchorGraphicsLayer, 3);
    (anchorGraphicsLayer as any).refresh();
    return anchorGraphicsLayer;
  }


  /**
   * Add click event listener on pin point layer and render specified popin on click
   */
  private _addPopinOnGraphicLayer(graphicLayer: Layer, type: string, selector: string) {

    graphicLayer.on('click', (evt: Event & { graphic: any }) => {
      evt.preventDefault();
      (this.webMap.infoWindow as PopupMobileConstructor).clearFeatures();

      const selectedItem = evt.graphic.attributes;
      let popinEl;
      if (type === 'asset') {
        this.setAssetSelected(selectedItem);
        popinEl = document.createElement(selector) as NgElement & WithProperties<PopInClusterComponent>;
      }

      if (type === 'beacon') {
        this.setBeaconSelected(selectedItem);
        popinEl = document.createElement(selector) as NgElement & WithProperties<PopInBeaconClusterComponent>;
      }

      if (type === 'anchor') {
        this.setAnchorSelected(selectedItem);
        popinEl = document.createElement(selector) as NgElement & WithProperties<PopInAnchorClusterComponent>;
      }

      if (type === 'history') {
        this.setAssetHistorySelected(selectedItem);
        popinEl = document.createElement(selector) as NgElement & WithProperties<PopInHistoryComponent>;
      }

      if (type === 'zone') {
        this.setZoneSelected(selectedItem);
        popinEl = document.createElement(selector) as NgElement & WithProperties<PopInZoneInfoComponent>;
      }

      popinEl.addEventListener('closed', () => document.body.removeChild(popinEl));

      // replace popin by PopInAssetInfoComponent
      const esriPopupWrapper = document.querySelector('.esriPopupWrapper');
      esriPopupWrapper.innerHTML = popinEl.outerHTML;
    });
  }

  /**
   * Add click event listener on cluster layer and render specified popin on click
   */
  private _addPopinOnClusterLayer(graphicLayer, selector: string) {

    graphicLayer.on('click', (evt) => {

      const graphicsValues = graphicLayer._clusterData;
      const selectedItem = evt.graphic.attributes;
      const clusterDatas = [];

      if (this.webMap.getZoom() === this.webMap.getMaxZoom()) {
        let popinEl;
        const clusterId = selectedItem.clusterId;
        for (const feature of graphicsValues) {
          if (feature.attributes['clusterId'] !== clusterId) {
            continue;
          }
          clusterDatas.push(feature);
        }

        if (selector === 'popin-element-cluster') {
          this.setClusterSelected(clusterDatas);
          popinEl = document.createElement(selector) as NgElement & WithProperties<PopInClusterComponent>;
        }

        if (selector === 'popin-element-beacon-cluster') {
          this.setBeaconClusterSelected(clusterDatas);
          popinEl = document.createElement(selector) as NgElement & WithProperties<PopInBeaconClusterComponent>;
        }

        if (selector === 'popin-element-anchor-cluster') {
          this.setAnchorClusterSelected(clusterDatas);
          popinEl = document.createElement(selector) as NgElement & WithProperties<PopInAnchorClusterComponent>;
        }

        if (selector === 'popin-element-history-cluster') {
          this.setHistoryClusterSelected(clusterDatas);
          popinEl = document.createElement(selector) as NgElement & WithProperties<PopInHistoryClusterComponent>;
        }

        popinEl.addEventListener('closed', () => document.body.removeChild(popinEl));
        // replace popin by PopInAssetInfoComponent
        const esriPopupWrapper = document.querySelector('.esriPopupWrapper');
        esriPopupWrapper.innerHTML = popinEl.outerHTML;
      }
    });
  }

  /**
   * This function is only for assets, and is triggered from asset list or tab view
   */
  public async showPopinOnGraphicLayer(asset: Asset) {
    if (this._mapViewEl.nativeElement.getAttribute('data-updating')) {
      // Data attribute is added to the map container when the map is updating.
      /** @see https://developers.arcgis.com/javascript/3/jsapi/map-amd.html */
      const handle = this.webMap.on('update-end', async () => {
        handle.remove();
        this._showPopinOnGraphicLayer(asset);
      });
    } else {
      this._showPopinOnGraphicLayer(asset);
    }
  }

  private async _showPopinOnGraphicLayer(asset: Asset) {
    this.webMap.infoWindow.clearFeatures();
    const coord = { lat: asset.last_event.lat, lng: asset.last_event.lng };
    const itemId = asset.id;
    const selector = 'popin-element-cluster';
    const center = new Point(coord.lng, coord.lat);
    await this.webMap.centerAndZoom(center, this.webMap.getMaxZoom()).promise;

    const assetGraphicsLayer = this.webMap.getLayer('assetGraphicsLayer') as GraphicsLayerConstructor & { geometry: GeometryConstructor & { x: number, y: number; }; };
    const graphics = assetGraphicsLayer.graphics as Array<GraphicConstructor & { geometry: GeometryConstructor & { x: number, y: number; }; }>;

    const graphicToDisplay = graphics.find((element) => element.attributes.id === itemId);

    const selectedAsset = graphicToDisplay.attributes;
    this.setAssetSelected(selectedAsset);

    const popinEl: NgElement & WithProperties<PopInClusterComponent> = document.createElement(selector) as any;
    popinEl.addEventListener('closed', () => document.body.removeChild(popinEl));

    // Replace popin by PopInAssetInfoComponent
    const esriPopupWrapper = document.querySelector('.esriPopupWrapper');
    esriPopupWrapper.innerHTML = popinEl.outerHTML;

    // Show popin
    const mapPoint = new Point(graphicToDisplay.geometry.x, graphicToDisplay.geometry.y, new SpatialReference(graphicToDisplay.geometry.spatialReference));
    this.webMap.infoWindow.show(mapPoint);
    this.resetSelectedItem(false);
  }

  /**
   * Display history popin and center and zoom on the history event
   */
  public async showPopinOnHistoryGraphicLayer(history: AssetEvent) {
    // Load the modules for the ArcGIS API for JavaScript

    this.webMap.infoWindow.clearFeatures();
    const coord = { lat: history.lat, lng: history.lng };
    const selector = 'popin-element-history';
    const center = new Point(coord.lng, coord.lat);

    // this.centerAndZoom = true;
    const historyGraphicsLayer = this.webMap.getLayer('historyGraphicsLayer') as GraphicsLayerConstructor;

    await this.webMap.centerAndZoom(center, this.webMap.getMaxZoom());

    const graphics = historyGraphicsLayer.graphics as Array<GraphicConstructor & { geometry: GeometryConstructor & { x: number, y: number } }>;

    const graphicToDisplay = graphics.find(function (element) {
      return element.attributes.order_time === history.order_time;
    });

    const selectedAssetHistory = graphicToDisplay.attributes;
    this.setAssetHistorySelected(selectedAssetHistory);

    const popinEl: NgElement & WithProperties<PopInHistoryComponent> = document.createElement(selector) as any;
    popinEl.addEventListener('closed', () => document.body.removeChild(popinEl));

    // replace popin by PopInAssetInfoComponent
    const esriPopupWrapper = document.querySelector('.esriPopupWrapper');
    esriPopupWrapper.innerHTML = popinEl.outerHTML;

    const mapPoint = new Point(graphicToDisplay.geometry.x, graphicToDisplay.geometry.y, new SpatialReference(graphicToDisplay.geometry.spatialReference));
    this.webMap.infoWindow.show(mapPoint);

  }

  public highlightPosition(x, y) {
    // Load the modules for the ArcGIS API for JavaScript
    const mapGraphicsLayer = this.webMap.graphics;
    mapGraphicsLayer.clear();

    const line = new SimpleLineSymbol();
    line.setStyle(SimpleMarkerSymbol.STYLE_SOLID);
    line.setColor(new Color([0, 255, 255]));
    line.setWidth(2);

    const symbol = new SimpleMarkerSymbol();
    symbol.setStyle(SimpleMarkerSymbol.STYLE_SQUARE);
    symbol.setColor(new Color([0, 0, 0, 0]));
    symbol.setSize(34);
    symbol.setOutline(line);

    const position = new Point(x, y);

    const graphic = new Graphic(position, symbol);
    mapGraphicsLayer.add(graphic);
    mapGraphicsLayer.redraw();
  }

  /**
   * Change the zone visibility
   */
  public setZonesVisibility(zonesDisplayed: boolean) {
    this.zoneDisplayed = zonesDisplayed;
    if (zonesDisplayed) {
      this.webMap.getLayer('zone')?.show();
    }
    if (!zonesDisplayed) {
      this.webMap.getLayer('zone')?.hide();
    }
  }

  /**
   * Set indoor layer visibility
   */
  public setIndoorLayerVisibility(visibility: boolean) {
    this._indoorVisible = visibility;

    // Load indoor layers lazily when user toggles Display Indoor ON
    if (visibility && !this._indoorDataset) {
      this.indoorSitesLoadError$.next(false);
      this.indoorLoading$.next(true);
      void this._ensureIndoorLayersLoaded();
    }

    this._applyIndoorVisibility();
  }

  public getIndoorSiteOptions(): IndoorSiteOption[] {
    return this.indoorSiteOptions$.value;
  }

  public getIndoorBuildingOptions(): IndoorBuildingOption[] {
    return this.indoorBuildingOptions$.value;
  }

  public getIndoorFloorOptions(): IndoorFloorOption[] {
    return this.indoorFloorOptions$.value;
  }

  public getSelectedIndoorSiteId(): string | null {
    return this._selectedIndoorSiteId;
  }

  public getSelectedIndoorBuildingId(): string | null {
    return this._selectedIndoorBuildingId;
  }

  public getSelectedIndoorFloorId(): string | null {
    return this._selectedIndoorFloorId;
  }

  public getSelectedIndoorBuilding(): IndoorBuilding | null {
    return this._selectedIndoorBuilding;
  }

  public setIndoorSite(siteId: string) {
    if (!this._indoorDataset?.sites.some((site) => site.id === siteId)) {
      return;
    }

    // Get the selected site to obtain its API SITE_ID
    const selectedSite = this._indoorDataset!.sites.find((site) => site.id === siteId);
    if (!selectedSite) {
      return;
    }

    // Immediately show the site layer by updating selection and visibility
    this._selectedIndoorSiteId = siteId;
    this.selectedIndoorSiteId$.next(siteId);

    // Reset building and floor selections
    this._selectedIndoorBuildingId = null;
    this.selectedIndoorBuildingId$.next(null);
    this._setIndoorBuildingSelected(null);
    this.indoorBuildingOptions$.next([]);
    this._selectedIndoorFloorId = null;
    this.selectedIndoorFloorId$.next(null);
    this.indoorFloorOptions$.next([]);

    this._focusOnIndoorPolygonCenter(selectedSite.rings, EsriMapService.INDOOR_SITE_MIN_ZOOM);

    // Apply visibility immediately so site layer becomes visible without waiting for API
    this._applyIndoorVisibility();
    this.indoorSiteBuildingsLoading$.next(true);

    // Load buildings for the selected site from the API
    this._indoorMapDataService.loadBuildingsForSite$(selectedSite.apiSiteId, this._indoorDataset!).subscribe({
      next: (buildings) => {
        // Update the dataset with the newly loaded buildings
        console.log(`Loaded ${buildings.length} buildings for site ${selectedSite.name} (API SITE_ID: ${selectedSite.apiSiteId})`);
        if (this._indoorDataset) {
          this._indoorDataset.buildings = buildings;
        }

        // Remove any existing building layers before adding the new ones
        const existingBuildingLayers = this.webMap.graphicsLayerIds
          .filter((id: string) => id.startsWith('indoor-data-building-'))
          .map((id: string) => this.webMap.getLayer(id));
        this.removeLayer(existingBuildingLayers);

        // Create map layers for the newly loaded buildings so they render on the map
        buildings.forEach((building) => {
          this.webMap.addLayer(this._createIndoorBuildingLayer(building), 0);
        });

        const buildingOptions = this._getIndoorBuildingOptionsForSite(siteId);
        this.indoorBuildingOptions$.next(buildingOptions);

        // Apply visibility to show buildings at appropriate zoom level
        this._applyIndoorVisibility();
        this.indoorSiteBuildingsLoading$.next(false);
      },
      error: (error) => {
        console.error('Failed to load buildings for site:', error);
        this.indoorSiteBuildingsLoading$.next(false);
      }
    });
  }

  public setIndoorBuilding(buildingId: string) {
    const building = this._indoorDataset?.buildings.find((item) => item.id === buildingId);
    if (!building) {
      this.indoorBuildingsDetailsLoading$.next(false);
      return;
    }

    if (this._selectedIndoorSiteId !== building.siteId) {
      this._selectedIndoorSiteId = building.siteId;
      this.selectedIndoorSiteId$.next(building.siteId);
    }

    const buildingOptions = this._getIndoorBuildingOptionsForSite(building.siteId);
    this.indoorBuildingOptions$.next(buildingOptions);

    // Immediately show the building layer by updating selection and visibility
    this._selectedIndoorBuildingId = buildingId;
    this.selectedIndoorBuildingId$.next(buildingId);
    this._setIndoorBuildingSelected(building);

    // Reset floor selection
    this._selectedIndoorFloorId = null;
    this.selectedIndoorFloorId$.next(null);
    this.indoorFloorOptions$.next([]);

    this._focusOnIndoorPolygonCenter(building.rings, EsriMapService.INDOOR_BUILDING_MIN_ZOOM);

    // Apply visibility immediately so building layer becomes visible without waiting for API
    this._applyIndoorVisibility();
    this.indoorBuildingsDetailsLoading$.next(true);

    // Load floors for the selected building from the API
    this._indoorMapDataService.loadFloorsForBuilding$(buildingId, this._indoorDataset!).subscribe({
      next: (floors) => {
        // Update the dataset with the newly loaded floors
        console.log(`Loaded ${floors.length} floors for building ${building.name}`);
        if (this._indoorDataset) {
          this._indoorDataset.floors = floors;
        }

        // Remove any existing floor layers from previous building selection
        const existingFloorLayers = this.webMap.graphicsLayerIds
          .filter((id: string) => id.startsWith('indoor-data-floor-'))
          .map((id: string) => this.webMap.getLayer(id));
        this.removeLayer(existingFloorLayers);

        // Floor layers are NOT created here - they are created on floor selection
        // with actual geometry from the API (areas + lines)

        const floorOptions = this._getIndoorFloorOptionsForBuilding(buildingId);
        this.indoorFloorOptions$.next(floorOptions);

        // Apply visibility to show floors at appropriate zoom level
        this._applyIndoorVisibility();
        this.indoorBuildingsDetailsLoading$.next(false);
      },
      error: (error) => {
        console.error('Failed to load floors for building:', error);
        // Still update floor options and visibility even if API fails
        const floorOptions = this._getIndoorFloorOptionsForBuilding(buildingId);
        this.indoorFloorOptions$.next(floorOptions);
        this._applyIndoorVisibility();
        this.indoorBuildingsDetailsLoading$.next(false);
      }
    });
  }

  public setIndoorFloor(floorId: string) {
    const floor = this._indoorDataset?.floors.find((item) => item.id === floorId);
    if (!floor) {
      this.indoorFloorDetailsLoading$.next(false);
      return;
    }

    this._selectedIndoorSiteId = floor.siteId;
    this.selectedIndoorSiteId$.next(floor.siteId);
    this.indoorBuildingOptions$.next(this._getIndoorBuildingOptionsForSite(floor.siteId));

    this._selectedIndoorBuildingId = floor.buildingId;
    this.selectedIndoorBuildingId$.next(floor.buildingId);
    const selectedBuilding = this._indoorDataset?.buildings.find((item) => item.id === floor.buildingId) ?? null;
    this._setIndoorBuildingSelected(selectedBuilding);

    this.indoorFloorOptions$.next(this._getIndoorFloorOptionsForBuilding(floor.buildingId));
    this._selectedIndoorFloorId = floorId;
    this.selectedIndoorFloorId$.next(floorId);

    if (selectedBuilding) {
      this._focusOnIndoorPolygonCenter(selectedBuilding.rings, EsriMapService.INDOOR_FLOOR_MIN_ZOOM);
    }

    // Load areas and lines for the selected floor from parallel API calls
    this.indoorFloorDetailsLoading$.next(true);
    this._indoorMapDataService.loadAreasAndLinesForFloor$(floorId, floor).subscribe({
      next: (updatedFloor) => {
        console.log(`Loaded ${updatedFloor.areas.length} areas and ${updatedFloor.lines.length} lines for floor ${floor.label}`);
        // Update the floor in the dataset with the fetched geometry
        if (this._indoorDataset) {
          const floorIndex = this._indoorDataset.floors.findIndex((f) => f.id === floorId);
          if (floorIndex >= 0) {
            this._indoorDataset.floors[floorIndex] = updatedFloor;
          }
        }
        // Remove ALL existing floor layers (from any previous floor selection)
        const allFloorLayers = this.webMap.graphicsLayerIds
          .filter((id: string) => id.startsWith('indoor-data-floor-'))
          .map((id: string) => this.webMap.getLayer(id))
          .filter(Boolean);
        this.removeLayer(allFloorLayers);

        // Create layers for the selected floor only, with actual geometry from API
        this.webMap.addLayer(this._createIndoorFloorAreaLayer(updatedFloor), 0);
        this.webMap.addLayer(this._createIndoorFloorLineLayer(updatedFloor), 1);

        this._applyIndoorVisibility();
        this.indoorFloorDetailsLoading$.next(false);
      },
      error: (error) => {
        console.error('Failed to load areas and lines for floor:', error);
        this._applyIndoorVisibility();
        this.indoorFloorDetailsLoading$.next(false);
      }
    });
  }

  public clearIndoorSelections() {
    this._selectedIndoorSiteId = null;
    this.selectedIndoorSiteId$.next(null);

    this._selectedIndoorBuildingId = null;
    this.selectedIndoorBuildingId$.next(null);
    this._setIndoorBuildingSelected(null);

    this._selectedIndoorFloorId = null;
    this.selectedIndoorFloorId$.next(null);

    this.indoorBuildingOptions$.next([]);
    this.indoorFloorOptions$.next([]);

    this.indoorSiteBuildingsLoading$.next(false);
    this.indoorBuildingsDetailsLoading$.next(false);
    this.indoorFloorDetailsLoading$.next(false);

    const floorLayers = this.webMap.graphicsLayerIds
      .filter((id: string) => id.startsWith('indoor-data-floor-'))
      .map((id: string) => this.webMap.getLayer(id))
      .filter(Boolean);
    this.removeLayer(floorLayers);

    this._applyIndoorVisibility();
  }

  private _setIndoorBuildingSelected(building: IndoorBuilding | null): Observable<IndoorBuilding | null> {
    this._selectedIndoorBuilding = building;
    if (building) {
      console.log('Clicked indoor building:', building.name);
    }
    this.selectedIndoorBuilding$.next(this._selectedIndoorBuilding);
    return this.selectedIndoorBuilding$.asObservable();
  }

    /**
   * Set enable and disable movement status
   */
    public setDisabledMovementStatus(assets: Asset[], isMovementStatusDisabled: boolean) {
        this._disabledMovementStatus = isMovementStatusDisabled;
        const isAssetLayer = this._getLayer('assetClusterFeatureLayer');
        if (isAssetLayer && isAssetLayer.visible) {
            this.displayAssets(assets);
            this._removeFlagedLayers();
        } else {
            this.displayHistory(this._lastHistory, this._lastAssetForHistory, true);
        }
      }

  /**
 * Enable or disable outdated horizon styling
 * and redraw the current asset layer.
 *
 * @param {Asset[]} assets Complete asset collection.
 * @param {boolean} disabledOutdatedHorizon
 * True to disable outdated marker styling.
 * @return {Promise<void>}
 */
  public async setDisabledOutdatedHorizon(
    assets: Asset[],
    disabledOutdatedHorizon: boolean,
    ): Promise<void> {
    this._disabledOutdatedHorizon =
        disabledOutdatedHorizon;

    const assetLayer =
        this._getLayer(
            'assetClusterFeatureLayer',
        );

    if (
        assetLayer &&
        assetLayer.visible
    ) {
        await this.displayAssets(
            assets ?? [],
        );

        this._removeFlagedLayers();
    }
  }


  /**
   * Change map layout
   */
  private async _changeMap(mapName: string) {

    // store extent in order to keep zoom on change base map
    const extent = this.webMap.extent;
    // Destroy the map for create a new one with a new basemap
    this.webMap.destroy();
    const esriMap = await arcgisUtils.createMap(
      this.maps[mapName],
      this._mapViewEl.nativeElement.id,
      {
        mapOptions: {
          showLabels: true,
          minZoom: 2,
          maxZoom: 22
        }
      }
    );
    this.webMap = esriMap.map;
    this.webMap.setExtent(extent);
    this._initWebMapConfig();
    this._recreateIndoorLayersAfterMapSwitch();
  }

  private _recreateIndoorLayersAfterMapSwitch() {
    if (!this._indoorDataset) {
      return;
    }

    this._indoorDataset.sites.forEach((site) => {
      this.webMap.addLayer(this._createIndoorSiteLayer(site), 0);
    });

    this._indoorDataset.buildings.forEach((building) => {
      this.webMap.addLayer(this._createIndoorBuildingLayer(building), 0);
    });

    if (this._selectedIndoorFloorId) {
      const selectedFloor = this._indoorDataset.floors.find((f) => f.id === this._selectedIndoorFloorId);
      if (selectedFloor) {
        this.webMap.addLayer(this._createIndoorFloorAreaLayer(selectedFloor), 0);
        this.webMap.addLayer(this._createIndoorFloorLineLayer(selectedFloor), 1);
      }
    }

    this._applyIndoorVisibility();
  }

  private async _ensureIndoorLayersLoaded() {
    if (!this.webMap) {
      this.indoorLoading$.next(false);
      return;
    }

    try {
      this._indoorDataset = await this._loadIndoorDataset();
      this.indoorSitesLoadError$.next(false);
      this.indoorSiteOptions$.next(this._indoorDataset.sites.map((site) => ({
        id: site.id,
        label: site.name,
      })));

      // Do NOT auto-select - keep initial state empty like Filter pane
      this.selectedIndoorSiteId$.next(null);
      this.selectedIndoorBuildingId$.next(null);
      this.selectedIndoorFloorId$.next(null);
      this.indoorBuildingOptions$.next([]);
      this.indoorFloorOptions$.next([]);

      this._createIndoorLayers(this._indoorDataset);
      this._applyIndoorVisibility();
    } catch (error) {
      console.error('Failed to load indoor sites from oneTelecom/sites:', error);
      this._indoorDatasetPromise = undefined as unknown as Promise<IndoorMapDataset>;
      this._indoorDataset = undefined as unknown as IndoorMapDataset;
      this.indoorSiteOptions$.next([]);
      this.indoorBuildingOptions$.next([]);
      this.indoorFloorOptions$.next([]);
      this.selectedIndoorSiteId$.next(null);
      this.selectedIndoorBuildingId$.next(null);
      this.selectedIndoorFloorId$.next(null);
      this.indoorSitesLoadError$.next(true);
    } finally {
      this.indoorLoading$.next(false);
    }
  }

  private _applyIndoorVisibility() {
    if (!this.webMap) {
      return;
    }

    const indoorLayers = this._getIndoorLayers();
    const zoom = this.webMap.getZoom();

    indoorLayers.forEach((layer) => {
      let shouldShow = false;

      if (this._indoorVisible) {
        switch (layer['indoorLayerType']) {
          case 'site':
            shouldShow = zoom >= EsriMapService.INDOOR_SITE_MIN_ZOOM
              && zoom < EsriMapService.INDOOR_BUILDING_MIN_ZOOM
              && (!layer['indoorSiteId'] || layer['indoorSiteId'] === this._selectedIndoorSiteId);
            break;
          case 'building':
            shouldShow = zoom >= EsriMapService.INDOOR_BUILDING_MIN_ZOOM
              && zoom < EsriMapService.INDOOR_FLOOR_MIN_ZOOM
              && !!this._selectedIndoorBuildingId
              && layer['indoorBuildingId'] === this._selectedIndoorBuildingId;
            break;
          case 'floor-area':
          case 'floor-line':
            shouldShow = zoom >= EsriMapService.INDOOR_FLOOR_MIN_ZOOM
              && layer['indoorBuildingId'] === this._selectedIndoorBuildingId
              && layer['indoorFloorId'] === this._selectedIndoorFloorId;
            break;
          default:
            shouldShow = false;
        }
      }

      layer.setVisibility(shouldShow);
    });
  }

  private _getIndoorLayers(): Array<LayerWithProps & { indoorSiteId?: string; indoorBuildingId?: string; indoorFloorId?: string; indoorLayerType?: string; setVisibility: (visible: boolean) => void }> {
    return this.webMap.graphicsLayerIds
      .map((id) => this.webMap.getLayer(id) as LayerWithProps & { indoorSiteId?: string; indoorBuildingId?: string; indoorFloorId?: string; indoorLayerType?: string; setVisibility: (visible: boolean) => void })
      .filter((layer) => layer?.arcgisProps?.title?.toLowerCase().startsWith('indoor'));
  }

  private _getIndoorLayerId(layerType: string, floorId?: string): string {
    return ['indoor-data', layerType, floorId].filter(Boolean).join('-');
  }

  private async _loadIndoorDataset(): Promise<IndoorMapDataset> {
    if (!this._indoorDatasetPromise) {
      this._indoorDatasetPromise = firstValueFrom(this._indoorMapDataService.getIndoorMap$());
    }
    return this._indoorDatasetPromise;
  }

  private _createIndoorLayers(indoorDataset: IndoorMapDataset) {
    const indoorLayers = this.webMap.graphicsLayerIds
      .filter((id) => id.startsWith('indoor-data-'))
      .map((id) => this.webMap.getLayer(id));
    this.removeLayer(indoorLayers);

    indoorDataset.sites.forEach((site) => {
      this.webMap.addLayer(this._createIndoorSiteLayer(site), 0);
    });

    indoorDataset.buildings.forEach((building) => {
      this.webMap.addLayer(this._createIndoorBuildingLayer(building), 0);
    });

    indoorDataset.floors.forEach((floor) => {
      this.webMap.addLayer(this._createIndoorFloorAreaLayer(floor), 0);
      this.webMap.addLayer(this._createIndoorFloorLineLayer(floor), 1);
    });
  }

  private _createIndoorSiteLayer(site: IndoorSite) {
    const layer = new GraphicsLayer({
      id: this._getIndoorLayerId('site', site.id),
      visible: false,
    }) as GraphicsLayerConstructor & LayerWithProps & { indoorLayerType?: string; indoorSiteId?: string };
    layer.arcgisProps = { title: `Indoor site ${site.name}` };
    layer.indoorLayerType = 'site';
    layer.indoorSiteId = site.id;

    const polygon = new Polygon(new SpatialReference({ wkid: 4326 }));
    site.rings.forEach((ring) => polygon.addRing(ring));
    const polygonWm = webMercatorUtils.geographicToWebMercator(polygon);

    const outline = new SimpleLineSymbol();
    outline.setColor(new Color([14, 116, 144, 0.95]));
    outline.setWidth(2);

    const symbol = new SimpleFillSymbol();
    symbol.setColor(new Color([125, 211, 252, 0.08]));
    symbol.setOutline(outline);
    layer.add(new Graphic(polygonWm, symbol, { type: 'indoor-site', name: site.name }));

    const label = new TextSymbol(site.name);
    label.setColor(new Color('#164E63'));
    label.font.setSize('14pt');
    label.font.setWeight('bold');
    label.font.setFamily('Inter, Roboto');
    layer.add(new Graphic(this._getPolygonCenter(site.rings), label, { type: 'indoor-site-label' }));

    return layer;
  }

  private _createIndoorBuildingLayer(building: IndoorMapDataset['buildings'][number]) {
    const layer = new GraphicsLayer({
      id: this._getIndoorLayerId('building', building.id),
      visible: false,
    }) as GraphicsLayerConstructor & LayerWithProps & { indoorLayerType?: string; indoorSiteId?: string; indoorBuildingId?: string };
    layer.arcgisProps = { title: `Indoor building ${building.name}` };
    layer.indoorLayerType = 'building';
    layer.indoorSiteId = building.siteId;
    layer.indoorBuildingId = building.id;

    const polygon = new Polygon(new SpatialReference({ wkid: 4326 }));
    building.rings.forEach((ring) => polygon.addRing(ring));
    const polygonWm = webMercatorUtils.geographicToWebMercator(polygon);

    const outline = new SimpleLineSymbol();
    outline.setColor(new Color([30, 41, 59, 0.95]));
    outline.setWidth(2);

    const symbol = new SimpleFillSymbol();
    symbol.setColor(new Color([148, 163, 184, 0.18]));
    symbol.setOutline(outline);
    layer.add(new Graphic(polygonWm, symbol, {
      type: 'indoor-building',
      buildingId: building.id,
      buildingName: building.name,
    }));

    layer.on('click', (evt: any) => {
      console.log('Indoor building clicked:', building.name, 'ID:', building.id);
      evt?.preventDefault?.();
      this.setIndoorBuilding(building.id);
    });

    return layer;
  }

  private _createIndoorFloorAreaLayer(floor: IndoorFloor) {
    const layer = new GraphicsLayer({
      id: this._getIndoorLayerId('floor-area', floor.id),
      visible: false,
    }) as GraphicsLayerConstructor & LayerWithProps & { indoorLayerType?: string; indoorSiteId?: string; indoorBuildingId?: string; indoorFloorId?: string };
    layer.arcgisProps = { title: `Indoor floor ${floor.label} area` };
    layer.indoorLayerType = 'floor-area';
    layer.indoorSiteId = floor.siteId;
    layer.indoorBuildingId = floor.buildingId;
    layer.indoorFloorId = floor.id;

    floor.areas.forEach((area) => {
      const polygon = new Polygon(new SpatialReference({ wkid: 4326 }));
      area.rings.forEach((ring) => polygon.addRing(ring));
      const polygonWm = webMercatorUtils.geographicToWebMercator(polygon);

      const outline = new SimpleLineSymbol();
      outline.setColor(new Color(area.outlineColor ?? [30, 64, 175, 0.95]));
      outline.setWidth(1);

      const symbol = new SimpleFillSymbol();
      symbol.setColor(new Color(area.fillColor));
      symbol.setOutline(outline);

      layer.add(new Graphic(polygonWm, symbol, {
        type: 'indoor-floor-area',
        buildingName: floor.buildingName,
        floorLabel: floor.label,
        areaId: area.id,
      }));
    });

    return layer;
  }

  private _createIndoorFloorLineLayer(floor: IndoorFloor) {
    const layer = new GraphicsLayer({
      id: this._getIndoorLayerId('floor-line', floor.id),
      visible: false,
    }) as GraphicsLayerConstructor & LayerWithProps & { indoorLayerType?: string; indoorSiteId?: string; indoorBuildingId?: string; indoorFloorId?: string };
    layer.arcgisProps = { title: `Indoor floor ${floor.label} line` };
    layer.indoorLayerType = 'floor-line';
    layer.indoorSiteId = floor.siteId;
    layer.indoorBuildingId = floor.buildingId;
    layer.indoorFloorId = floor.id;

    floor.lines.forEach((lineFeature) => {
      const polyline = new Polyline(new SpatialReference({ wkid: 4326 }));
      lineFeature.paths.forEach((path) => polyline.addPath(path));
      const polylineWm = webMercatorUtils.geographicToWebMercator(polyline as any);

      const symbol = new SimpleLineSymbol();
      symbol.setColor(new Color(lineFeature.color ?? [15, 23, 42, 0.9]));
      symbol.setWidth(1);

      layer.add(new Graphic(polylineWm, symbol, {
        type: 'indoor-floor-line',
        buildingName: floor.buildingName,
        floorLabel: floor.label,
      }));
    });

    return layer;
  }

  private _getPolygonCenter(rings: Array<Array<[number, number]>>) {
    return this._toWebMercatorPoint(this._getPolygonCenterCoordinates(rings));
  }

  private _getPolygonCenterCoordinates(rings: Array<Array<[number, number]>>): [number, number] {
    const points = rings.flat();
    const longitudes = points.map(([longitude]) => longitude);
    const latitudes = points.map(([, latitude]) => latitude);
    return [
      (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
      (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
    ];
  }

  private _focusOnIndoorPolygonCenter(rings: Array<Array<[number, number]>>, zoomLevel: number): void {
    if (!this.webMap) {
      return;
    }

    const points = rings.flat();
    if (points.length === 0) {
      return;
    }

    const [longitude, latitude] = this._getPolygonCenterCoordinates(rings);
    const center = new Point(longitude, latitude, new SpatialReference({ wkid: 4326 }));
    this.webMap.centerAndZoom(center, zoomLevel);
  }

  private _getIndoorBuildingOptionsForSite(siteId: string): IndoorBuildingOption[] {
    return this._indoorDataset?.buildings
      .filter((building) => building.siteId === siteId)
      .map((building) => ({
        id: building.id,
        label: building.name,
        siteId: building.siteId,
        siteName: building.siteName,
      })) ?? [];
  }

  private _getIndoorFloorOptionsForBuilding(buildingId: string): IndoorFloorOption[] {
    return this._indoorDataset?.floors
      .filter((floor) => floor.buildingId === buildingId)
      .map((floor) => ({
        id: floor.id,
        label: floor.label,
        siteId: floor.siteId,
        siteName: floor.siteName,
        buildingId: floor.buildingId,
        buildingName: floor.buildingName,
        level: floor.level,
      })) ?? [];
  }


  private _toWebMercatorPoint([longitude, latitude]: [number, number]) {
    const point = new Point(longitude, latitude, new SpatialReference({ wkid: 4326 }));
    return webMercatorUtils.geographicToWebMercator(point);
  }

  /**
   * Switch Map
   */
  public async switchMap(isPressed: boolean) {
    if (isPressed) {
      await this._changeMap('satellite');
    } else {
      await this._changeMap('openStreetMap');
    }
  }


  /**
   * Change the beacons visibility
   */
  public setBeaconsVisibility(beaconsDisplayed: boolean) {
    this._beaconsVisible = beaconsDisplayed;

    if (beaconsDisplayed) {
      this.webMap.getLayer('beaconClusterFeatureLayer').show();
      this.webMap.getLayer('beaconGraphicsLayer').show();

    }
    if (!beaconsDisplayed) {
      this.webMap.getLayer('beaconClusterFeatureLayer').hide();
      this.webMap.getLayer('beaconGraphicsLayer').hide();
    }
  }

  /**
 * Change the beacon label visibility
 */
  public setBeaconLabelsVisibility(beaconLabelsDisplayed: boolean) {
    this.webMap.getLayer('beaconLabelsGraphicsLayer')[beaconLabelsDisplayed ? 'show' : 'hide']();
  }

  /**
   * Change the anchor visibility
   */
  public setAnchorsVisibility(anchorDisplayed: boolean) {
    this._anchorsVisible = anchorDisplayed;
    if (anchorDisplayed) {
      this.webMap.getLayer('anchorClusterFeatureLayer').show();
      this.webMap.getLayer('anchorGraphicsLayer').show();

    }
    if (!anchorDisplayed) {
      this.webMap.getLayer('anchorClusterFeatureLayer').hide();
      this.webMap.getLayer('anchorGraphicsLayer').hide();
    }
  }

  /**
   * Change the anchor label visibility
   */
  public setAnchorLabelsVisibility(anchorLabelsDisplayed: boolean) {
    this.webMap.getLayer('anchorLabelsGraphicsLayer')[anchorLabelsDisplayed ? 'show' : 'hide']();
  }

  /**
  * Set the asset label visibility
  */
  public setAssetLabelsVisibility(assetLabelsDisplayed: boolean) {
    this._assetLabelsVisible = assetLabelsDisplayed;
    this.webMap.getLayer('assetLabelsGraphicsLayer')[assetLabelsDisplayed ? 'show' : 'hide']();
  }

  /**
   * Util to remove several layers
   */
  public removeLayer(tabLayer: any[]) {
    for (const layer of tabLayer) {
      if (layer) {
        this.webMap.removeLayer(layer);
      }
    }
  }

  /**
   * Util to remove all layers
   */
  public removeAllLayer() {
    const assetClusterFeatureLayer = this._getLayer('assetClusterFeatureLayer');
    const beaconClusterFeatureLayer = this.webMap.getLayer('beaconClusterFeatureLayer');
    const anchorClusterFeatureLayer = this.webMap.getLayer('anchorClusterFeatureLayer');
    const historyAssetClusterFeatureLayer = this.webMap.getLayer('historyAssetClusterFeatureLayer');
    const historyAssetBeaconClusterFeatureLayer = this.webMap.getLayer('historyAssetBeaconClusterFeatureLayer');
    const assetGraphicsLayer = this.webMap.getLayer('assetGraphicsLayer');
    const beaconGraphicsLayer = this.webMap.getLayer('beaconGraphicsLayer');
    const anchorGraphicsLayer = this.webMap.getLayer('anchorGraphicsLayer');
    const graphHistoryLayer = this.webMap.getLayer('graphHistoryLayer');
    /* Labels Layers */
    const assetLabelsGraphicsLayer = this.webMap.getLayer('assetLabelsGraphicLayer');
    const anchorLabelsGraphicsLayer = this.webMap.getLayer('anchorLabelsGraphicsLayer');
    const beaconLabelsGraphicsLayer = this.webMap.getLayer('beaconLabelsGraphicsLayer');

    const tabLayer = [assetClusterFeatureLayer, beaconClusterFeatureLayer, historyAssetBeaconClusterFeatureLayer, anchorClusterFeatureLayer, historyAssetClusterFeatureLayer,
      assetGraphicsLayer, assetLabelsGraphicsLayer, beaconGraphicsLayer, anchorGraphicsLayer, graphHistoryLayer, anchorLabelsGraphicsLayer, beaconLabelsGraphicsLayer];
    this.removeLayer(tabLayer);
  }

  /**
   * Util to hide all layers without deleting them
   */
  public hideAllLayer() {
    const allLayersIds = [
      'assetClusterFeatureLayer',
      'beaconClusterFeatureLayer',
      'anchorClusterFeatureLayer',
      'historyAssetClusterFeatureLayer',
      'historyAssetBeaconClusterFeatureLayer',
      'assetGraphicsLayer',
      'assetLabelsGraphicsLayer',
      'beaconGraphicsLayer',
      'anchorGraphicsLayer',
      'graphHistoryLayer',
      'beaconLabelsGraphicsLayer',
      'anchorLabelsGraphicsLayer'
    ];
    allLayersIds.forEach((layerId) => {
      if (layerId) {
        this._getLayer(layerId)?.hide();
      }
    });
  }

  /**
   * Display the history layer and hide other layers
   */
  public async displayHistory(history: AssetEvent[], asset: Asset, centerAndZoom?: boolean) {
    this.setAssetSelected(asset);
    this._lastHistory = history;
    this._lastAssetForHistory = asset;
    this.hideAllLayer();
    // remove history layers is exist
    this.removeLayer([this.webMap.getLayer('historyGraphicsLayer'), this.webMap.getLayer('graphHistoryLayer'), this.webMap.getLayer('historyAssetClusterFeatureLayer')]);
    // create layers
    const historyClusterLayer = await this._createHistoryAssetClustering(history);
    const historyGraphicLayer = await this._createHistoryAssetGraphicsLayer(historyClusterLayer, asset);
    // add popin on click
    this._addPopinOnGraphicLayer(historyGraphicLayer, 'history', 'popin-element-history');
    this._addPopinOnClusterLayer(historyClusterLayer, 'popin-element-history-cluster');
    // center and zoom on history
    if (centerAndZoom) {
      this.centerAndZoomOnHistory(history);
    }
    this.resetSelectedItem(true);
  }

  /**
   * Close the history
   */
  public closeHistory() {
    this.resetSelectedItem(false);
    this._removeHistoryMarkers();
  }

  /**
   * Create the history cluster layer
   */
  private _createHistoryAssetClustering(assetHistory) {
    return new Promise((resolve) => {
      const polylinePoints = [];
      const pointClustering = [];

      const graphLayer = new GraphicsLayer({
        id: 'graphHistoryLayer',
        infoTemplate: new PopupTemplate({})
      });

      for (const history of assetHistory) {
        if (!history.lat || !history.lng) {
          continue;
        }
        const point = new Point(history.lng, history.lat);
        const webMercator = webMercatorUtils.geographicToWebMercator(point);
        pointClustering.push({
          x: webMercator.x,
          y: webMercator.y,
          attributes: { ...history, ...{ type: 'asset' } }
        });
        polylinePoints.push({ x: point.x, y: point.y });
      }

      const lineSymbol = new SimpleLineSymbol({
        'cap': 'round',
        'color': new Color([1, 17, 51, 0.75]),
        'join': 'round',
        'width': 1
      });

      lineSymbol.setMarker({
        style: 'arrow',
        placement: 'end'
      });

      for (let i = 0; i < polylinePoints.length - 1; i++) {
        if (polylinePoints[i].x !== polylinePoints[i + 1].x && polylinePoints[i].y !== polylinePoints[i + 1].y) {
          const middleX = (polylinePoints[i].x + polylinePoints[i + 1].x) / 2;
          const middleY = (polylinePoints[i].y + polylinePoints[i + 1].y) / 2;

          const polyline1 = new Polyline({
            'paths': [[[polylinePoints[i].x, polylinePoints[i].y], [middleX, middleY]]],
            'spatialReference': { 'wkid': 4326 }
          });

          const polyline2 = new Polyline({
            'paths': [[[middleX, middleY], [polylinePoints[i + 1].x, polylinePoints[i + 1].y]]],
            'spatialReference': { 'wkid': 4326 }
          });

          const graphic1 = new Graphic(polyline1, lineSymbol);
          graphLayer.add(graphic1);

          const graphic2 = new Graphic(polyline2, lineSymbol);
          graphLayer.add(graphic2);
        }
      }

      const historyClusterFeatureLayer = new ClusterFeatureLayer({
        'data': pointClustering,
        'distance': 70,
        'id': 'historyAssetClusterFeatureLayer',
        'labelColor': '#fff',
        'showSingles': false
      });

      const defaultSym = new Symbol();
      const [small, medium, large, xlarge] = this.getMarkersHistory([50, 75, 90, 110]);

      const renderer = new ClassBreaksRenderer(defaultSym, 'clusterCount');

      // Break values - can adjust easily
      renderer.addBreak(2, 50, small);
      renderer.addBreak(50, 250, medium);
      renderer.addBreak(250, 1000, large);
      renderer.addBreak(1000, 50000, xlarge);

      // Providing a ClassBreakRenderer is also optional
      historyClusterFeatureLayer.setRenderer(renderer);

      this.webMap.addLayer(graphLayer);
      this.webMap.addLayer(historyClusterFeatureLayer);

      // Refresh else the layers do not display on pan end
      (graphLayer as any).refresh();
      historyClusterFeatureLayer.refresh();

      resolve(historyClusterFeatureLayer);
    });
  }

  /**
   * Create the history pinpoint layer
   */
  public _createHistoryAssetGraphicsLayer(historyAssetClusterFeatureLayer, asset: Asset) {
    this._lastAssetForHistory = asset; // save asset used for history generation in order to reuse it for zooming
    return new Promise<Layer>((resolve) => {

      // Load the modules for the ArcGIS API for JavaScript
      const historyGraphicsLayer = new GraphicsLayer({
        id: 'historyGraphicsLayer',
        infoTemplate: new PopupTemplate({})
      });

      if (historyAssetClusterFeatureLayer) {

        for (const cluster of historyAssetClusterFeatureLayer.graphics) {
          let clusterId = '';
          // This means that the asset is not in a cluster
          if (cluster.attributes['clusterCount'] === 1) {
            clusterId = cluster.attributes['clusterId'];
            for (const feature of historyAssetClusterFeatureLayer._clusterData) {
              const attributes = feature.attributes;
              if (attributes.type !== 'asset' || attributes['clusterId'] !== clusterId) {
                continue;
              }

              const point = new Point(attributes['lng'], attributes['lat'], new SpatialReference({ wkid: 4326 }));
              let pictoColor = this._mapScopeService.getPictoColor(attributes, this._disabledMovementStatus, asset.family.scope);
              if (attributes.accuracy > 20 && !this._disabledMovementStatus) {
                pictoColor = [...pictoColor, this.STALE_PINPOINT_ALPHA];
              }
              const markerPicto = this._createSymbol(
                this._mapScopeService.getPictoByAsset(asset),
                pictoColor,
                this._mapScopeService.isPictoOutlined(attributes),
                this._mapScopeService.getOutlineColor(attributes, this._disabledMovementStatus),
              );
              const backgroundPicto = this._createSymbol(
                this._mapScopeService.getBackGroundPicto(),
                this._mapScopeService.getBackGroundColor(attributes, this._disabledMovementStatus),
                false,
                null,
              );
              const marker = new Graphic(point, markerPicto, attributes);
              const background = new Graphic(point, backgroundPicto, attributes);
              historyGraphicsLayer.add(background);
              historyGraphicsLayer.add(marker);
            }
          } else {
            if (this.webMap.getZoom() === this.webMap.getMaxZoom()) {
              clusterId = cluster.attributes['clusterId'];
              for (const feature of historyAssetClusterFeatureLayer._clusterData) {
                const attributes = feature.attributes;
                if (attributes.type !== 'asset' || attributes['clusterId'] !== clusterId) {
                  continue;
                }
                const point = new Point(attributes['lng'], attributes['lat'], new SpatialReference({ wkid: 4326 }));
                const graphic = new Graphic(point, new Symbol(), attributes);
                historyGraphicsLayer.add(graphic);
              }
            }
          }
        }

        this.webMap.addLayer(historyGraphicsLayer);
        (historyGraphicsLayer as any).refresh();
        resolve(historyGraphicsLayer);
      }

    });
  }

  public setAssetSelected(asset: Asset): Observable<any> {
    this._selectedAsset = asset;
    this.setClusterSelected([asset]);
    this.selectedAsset$.next(this._selectedAsset);
    this.highlightPosition(asset.last_event['lng'], asset.last_event['lat']);
    return this.selectedAsset$.asObservable();
  }

  private setClusterSelected(clusterDatas): Observable<any> {
    this.selectedCluster = clusterDatas;
    this.selectedCluster$.next(this.selectedCluster);
    return this.selectedCluster$.asObservable();
  }

  private setHistoryClusterSelected(clusterDatas): Observable<any> {
    this.selectedHistoryCluster = clusterDatas;
    this.selectedHistoryCluster$.next(this.selectedHistoryCluster);
    return this.selectedHistoryCluster$.asObservable();
  }

  private setBeaconSelected(beacon: any): Observable<any> {
    this.selectedBeacon = beacon;
    this.selectedBeaconCluster$.next([beacon]);
    this.selectedBeacon$.next(this.selectedBeacon);
    this.highlightPosition(beacon.lng, beacon.lat);
    return this.selectedBeacon$.asObservable();
  }

  private setAnchorSelected(anchor: Anchor): Observable<any> {
    this.selectedAnchor = anchor;
    this.selectedAnchorCluster$.next([anchor]);
    this.selectedAnchor$.next(this.selectedAnchor);
    this.highlightPosition(anchor.longitude, anchor.latitude);
    return this.selectedAnchor$.asObservable();
  }

  private setZoneSelected(zone): Observable<any> {
    this.selectedZone = zone;
    this.selectedZone$.next(this.selectedZone);
    return this.selectedZone$.asObservable();
  }

  private setBeaconClusterSelected(clusterDatas: any): Observable<any> {
    this.selectedBeaconCluster = clusterDatas;
    this.selectedBeaconCluster$.next(this.selectedBeaconCluster);
    return this.selectedBeaconCluster$.asObservable();
  }

  private setAnchorClusterSelected(clusterDatas: any): Observable<any> {
    this.selectedAnchorCluster = clusterDatas;
    this.selectedAnchorCluster$.next(this.selectedAnchorCluster);
    return this.selectedAnchorCluster$.asObservable();
  }

  private setAssetHistorySelected(asset_history): Observable<any> {
    this._selectedAssetHistory = asset_history;
    this.selectedAssetHistory$.next(this._selectedAssetHistory);
    this.highlightPosition(asset_history.lng, asset_history.lat);
    return this.selectedAssetHistory$.asObservable();
  }

  private getMarkersAsset(markersSizes: number[]) {
    const markers = [];
    for (const markerSize of markersSizes) {
      markers.push(new SimpleMarkerSymbol('circle', markerSize,
        new SimpleLineSymbol(SimpleLineSymbol.STYLE_SOLID, new Color([1, 17, 51, 0.55]), 3),
        new Color([1, 17, 51, 0.75])));
    }

    return markers;
  }

  private getMarkersBeacon(markersSizes: number[]) {
    const markers = [];
    for (const markerSize of markersSizes) {
      markers.push(new SimpleMarkerSymbol('circle', markerSize,
        new SimpleLineSymbol(SimpleLineSymbol.STYLE_SOLID, new Color([0, 133, 173, 0.55]), 3),
        new Color([0, 133, 173, 0.75])));
    }

    return markers;
  }

  private getMarkersAnchor(markersSizes: number[]) {
    const markers = [];
    for (const markerSize of markersSizes) {
      markers.push(new SimpleMarkerSymbol('circle', markerSize,
        new SimpleLineSymbol(SimpleLineSymbol.STYLE_SOLID, new Color([127, 127, 127, 0.55]), 3),
        new Color([127, 127, 127, 0.75])));
    }

    return markers;
  }

  private getMarkersHistory(markersSizes: number[]) {
    const markers = [];
    for (const markerSize of markersSizes) {
      markers.push(new SimpleMarkerSymbol('circle', markerSize,
        new SimpleLineSymbol(SimpleLineSymbol.STYLE_SOLID, new Color([1, 17, 51, 0.55]), 3),
        new Color([1, 17, 51, 0.75])));
    }

    return markers;
  }

  private _removeHistoryMarkers() {
    // Remove history point layer
    this.webMap.removeLayer(this.webMap.getLayer('historyGraphicsLayer'));
    // Remove history line layer
    this.webMap.removeLayer(this.webMap.getLayer('graphHistoryLayer'));
    // Remove history cluster layer
    this.webMap.removeLayer(this.webMap.getLayer('historyAssetClusterFeatureLayer'));
  }

  /**
   * Center and zoom on assets
   */
  public centerAndZoomOnAssets(assets: Asset[]) {

    const cleanedAssets = assets.filter(asset => asset.last_event && asset.last_event.lng && asset.last_event.lat && ((asset.tracker) || (asset.last_event.source === SignalSources.GO2SCAN)));
    if (cleanedAssets.length) {
      if (cleanedAssets.length === 1) {
        const center = new Point(cleanedAssets[0].last_event.lng, cleanedAssets[0].last_event.lat);
        this.webMap.centerAndZoom(center, this.webMap.getMaxZoom());
      } else {
        const latMin = Math.min(...cleanedAssets.map(asset => asset.last_event.lat));
        const latMax = Math.max(...cleanedAssets.map(asset => asset.last_event.lat));
        const lngMin = Math.min(...cleanedAssets.map(asset => asset.last_event.lng));
        const lngMax = Math.max(...cleanedAssets.map(asset => asset.last_event.lng));

        this.webMap.setExtent(new Extent(lngMin, latMin, lngMax, latMax, new SpatialReference({ wkid: 4326 })), true);
      }
    }
  }

  /**
   * Center and zoom on history events
   */
  public centerAndZoomOnHistory(history: AssetEvent[]) {

    const cleanedHistory = history.filter(position => position.lat && position.lng);
    if (cleanedHistory.length) {
      if (cleanedHistory.length === 1) {
        const center = new Point(cleanedHistory[0].lng, cleanedHistory[0].lat);
        this.webMap.centerAndZoom(center, this.webMap.getMaxZoom());
      } else {
        const latMin = Math.min(...cleanedHistory.map(position => position.lat));
        const latMax = Math.max(...cleanedHistory.map(position => position.lat));
        const lngMin = Math.min(...cleanedHistory.map(position => position.lng));
        const lngMax = Math.max(...cleanedHistory.map(position => position.lng));

        this.webMap.setExtent(new Extent(lngMin, latMin, lngMax, latMax, new SpatialReference({ wkid: 4326 })), true);
      }
    }
  }

  /**
   * Un select an item
   */
  public resetSelectedItem(bool: boolean): Observable<boolean> {
    this.unselectedItem = bool;
    this.unselectedItem$.next(this.unselectedItem);

    if (bool) {
      this._setIndoorBuildingSelected(null);
    }

    if (bool) {
      this.webMap.infoWindow.clearFeatures();
      const mapGraphicsLayer = this.webMap.graphics;
      mapGraphicsLayer.clear();
    }

    return this.unselectedItem$.asObservable();
  }

  /**
   * Create a pin with no position from an SVG
   * @svgPath Svg icon to represent
   * @svgColor Svg color to apply
   * @outline Boolean to know if the pin need an outline or not
   * @SimpleMarkerSymbol Class from the Arcgis API/library to create the pin
   * @Color Class from the Arcgis API/library to create a color
   */
  private _createSymbol(svgPath: string, svgColor: any, outline: boolean, outlineColor: any) {
    const symbol = new SimpleMarkerSymbol();
    symbol.setPath(svgPath);
    symbol.setColor(new Color(svgColor));
    symbol.setSize(30);
    symbol.setOffset(0, symbol.size / 2);
    if (outline) {
      symbol.outline.setColor(new Color(outlineColor));
      symbol.outline.setWidth(1);
    } else {
      symbol.setOutline(null);
    }
    return symbol;
  }

  private _attachOnClickEvent(webMap: MapConstructor & { infoWindow: PopupMobileConstructor }) {
    webMap.on('click', (evt: Event) => {
      if (!evt.defaultPrevented) {
        this.resetSelectedItem(true);
      }
    });
  }

  /**
 * Check Min or Max Zoom and disable when its reached there higher limit
 */
  private _checkMinOrMaxZoom() {
    const elZoomOutButton = document.getElementsByClassName('esriSimpleSliderDecrementButton')[0];
    elZoomOutButton?.classList.toggle('esriSimpleSliderDisabledButton', this.webMap.getZoom() === this.webMap.getMinZoom());

    const elZoomInButton = document.getElementsByClassName('esriSimpleSliderIncrementButton')[0];
    elZoomInButton?.classList.toggle('esriSimpleSliderDisabledButton', this.webMap.getZoom() === this.webMap.getMaxZoom());

  }

  /**
   * Centers the map on the provided anchors and zooms to the maximum level.
   * @param anchors Array of anchor points to center on.
   */
  public centerAndZoomOnAnchors(anchors: Anchor[]) {

    const cleanedAnchor = anchors.filter(anchor => anchor.longitude && anchor.latitude);
    if (cleanedAnchor.length) {
      if (cleanedAnchor.length === 1) {
        const center = new Point(cleanedAnchor[0].longitude, cleanedAnchor[0].latitude);
        this.webMap.centerAndZoom(center, this.webMap.getMaxZoom());
      }
    }
  }

    /*
    * Centers the map on the provided beacons and zooms to the maximum level.
    * @param beacons Array of beacon points to center on.
    */
  public centerAndZoomOnBeacons(beacons: EsriBeacon[]) {

    const cleanedAnchor = beacons.filter(beacon => beacon.geometry.x && beacon.geometry.y);
    if (cleanedAnchor.length) {
      if (cleanedAnchor.length === 1) {
        const center = new Point(cleanedAnchor[0].geometry.x, cleanedAnchor[0].geometry.y);
        this.webMap.centerAndZoom(center, this.webMap.getMaxZoom());
      }
    }
  }
}
